import { isLoggedIn, productCache, getCurrentMarketContext, isSuspicious } from '../utilities/utils';
import { STORES, getItem, putItem } from '../utilities/indexedDB.js';

const BOARD_CONFIG = {
  DEFAULT_BOARD_ID: 1,
  MAX_BOARDS: 12,
  MAX_BOARD_NAME_LENGTH: 25,
  DEFAULT_BOARD_NAME: 'My Wishlist',
  DEFAULT_BOARD_COLOUR: window.wishlist_colours?.find((colour) => colour.default === true)?.colour || '#FF6B6B',
  DEFAULT_BOARD_EMOJI: window.wishlist_emojis?.find((emoji) => emoji.default === true)?.emoji || '❤️',
  VERSION: '2.0',
};

const OPERATION_TYPES = {
  ADD: 'add',
  REMOVE: 'remove',
  REMOVE_ALL_ITEMS: 'removeAllItems',
  UPDATE_VARIANT: 'updateVariant',
  CREATE_BOARD: 'createBoard',
  EDIT_BOARD: 'editBoard',
  DELETE_BOARD: 'deleteBoard',
  RENAME_BOARD: 'renameBoard',
  MOVE_PRODUCT: 'moveProduct',
};

const WISHLIST_GET_ENDPOINT = 'https://australia-southeast1-foxtware.cloudfunctions.net/shopifyWishlistGet';
const WISHLIST_OPERATIONS_ENDPOINT = 'https://australia-southeast1-foxtware.cloudfunctions.net/shopifyWishlistOperations';

/**
 * Creates a default board object
 * @returns {Object} Default board
 */
const createDefaultBoard = () => ({
  id: BOARD_CONFIG.DEFAULT_BOARD_ID,
  name: BOARD_CONFIG.DEFAULT_BOARD_NAME,
  colour: BOARD_CONFIG.DEFAULT_BOARD_COLOUR,
  emoji: BOARD_CONFIG.DEFAULT_BOARD_EMOJI,
  items: [],
  active: true,
  createdAt: new Date().toISOString(),
});

/**
 * Ensures the default board (ID 1) always exists in the wishlist
 * @param {Object} wishlist - Wishlist object to validate
 * @returns {Object} Wishlist with guaranteed default board
 */
const ensureDefaultBoard = (wishlist) => {
  if (!wishlist.boards || !Array.isArray(wishlist.boards)) {
    wishlist.boards = [];
  }

  const hasDefaultBoard = wishlist.boards.some((board) => board.id === BOARD_CONFIG.DEFAULT_BOARD_ID);

  if (!hasDefaultBoard) {
    wishlist.boards.unshift(createDefaultBoard());
  }

  return wishlist;
};

/**
 * Finds the next available board ID from 1-10, filling gaps
 * @param {Array} existingBoards - Array of existing board objects
 * @returns {number|null} Next available ID (1-10) or null if all slots taken
 */
const getNextAvailableBoardId = (existingBoards) => {
  const usedIds = new Set(existingBoards.map((board) => board.id));

  for (let id = 1; id <= BOARD_CONFIG.MAX_BOARDS; id++) {
    if (!usedIds.has(id)) {
      return id;
    }
  }

  return null;
};

/**
 * Validates that a board ID is within the acceptable range (1-10)
 * @param {number} boardId - Board ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
const isValidBoardId = (boardId) => {
  return typeof boardId === 'number' && boardId >= 1 && boardId <= BOARD_CONFIG.MAX_BOARDS;
};

/**
 * Default structure for a new wishlist
 * @type {Object}
 * @property {number} count - Total number of products across all boards
 * @property {Object} allProductIds - Object/Map of all product IDs in wishlist for quick lookup (productId: true)
 * @property {Object} productData - Map of product IDs to cached product and variant info
 * @property {Array} boards - Array of boards containing product IDs with optional variant selections
 */
const DEFAULT_WISHLIST = {
  id: 'wishlist',
  count: 0,
  allProductIds: {},
  productData: {},
  boards: [createDefaultBoard()],
};

const LOGIN_STATE_KEY = 'wishlist_login_state';
const PENDING_OPERATIONS_KEY = 'wishlist_pending_operations';

const STOREFRONT_API_URL = `${window.Shopify.routes.root}api/2025-07/graphql.json`;
const STOREFRONT_ACCESS_TOKEN = window.theme.storefrontAccessToken || '';

// Global map to track ongoing product fetches to prevent duplicate queries
const ongoingProductQueries = new Map();

// Request-level deduplication
let wishlistDataVersion = 0;
const ongoingRequests = new Map();

// Cache invalidation
const invalidateCache = () => {
  wishlistDataVersion++;
  ongoingRequests.clear();
};

const PRODUCT_WITH_SIBLINGS_QUERY = `
  query getProductWithSiblingsById($id: ID!, $country: CountryCode) @inContext(country: $country) {
   product(id: $id) {
      id
      title
      handle
      description
      vendor
      type: productType
      created_at: createdAt
      published_at: publishedAt
      available: availableForSale
      tags
      onlineStoreUrl
      options {
        id
        name
        values
      }

      coming_soon_metafield: metafield(namespace: "state", key: "coming_soon") {
        value
      }

      just_dropped_metafield: metafield(namespace: "state", key: "just_dropped") {
        value
      }

      variants(first: 10) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compare_at_price: compareAtPrice {
              amount
              currencyCode
            }
            available: availableForSale
            selectedOptions {
              name
              value
            }
            image {
              id
              url
              altText
              width
              height
            }
            featured_image: image {
              id
              url
              altText
              width
              height
            }
            sku
            barcode
            weight
            weight_unit: weightUnit
            requires_shipping: requiresShipping
            taxable
          }
        }
      }

      images(first: 10) {
        edges {
          node {
            id
            url
            altText
            width
            height
          }
        }
      }

      featured_image: featuredImage {
        id
        url
        altText
        width
        height
      }

      featured_media: featuredImage {
        id
        url
        altText
        width
        height
      }

      media(first: 10) {
        edges {
          node {
            mediaContentType
            alt
            ... on MediaImage {
              id
              image {
                url
                altText
                width
                height
              }
            }
            ... on Video {
              id
              sources {
                url
                mimeType
                format
                height
                width
              }
            }
            ... on ExternalVideo {
              id
              embeddedUrl
            }

          }
        }
      }

      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }

      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }

      url: onlineStoreUrl

      siblings: metafield(namespace: "related_products", key: "siblings") {
        value
        type
        references(first: 25) {
          edges {
            node {
              ... on Product {
                id
                title
                handle
                description
                content: description
                vendor
                type: productType
                created_at: createdAt
                published_at: publishedAt
                online_store_url: onlineStoreUrl
                available: availableForSale
                tags
                options {
                  id
                  name
                  values
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      compare_at_price: compareAtPrice {
                        amount
                        currencyCode
                      }
                      available: availableForSale
                      selectedOptions {
                        name
                        value
                      }
                      image {
                        id
                        url
                        altText
                        width
                        height
                      }
                      featured_image: image {
                        id
                        url
                        altText
                        width
                        height
                      }
                      sku
                      barcode
                      weight
                      weight_unit: weightUnit
                      requires_shipping: requiresShipping
                      taxable
                    }
                  }
                }
                images(first: 10) {
                  edges {
                    node {
                      id
                      url
                      altText
                      width
                      height
                    }
                  }
                }
                featured_image: featuredImage {
                  id
                  url
                  altText
                  width
                  height
                }
                featured_media: featuredImage {
                  id
                  url
                  altText
                  width
                  height
                }
                media(first: 10) {
                  edges {
                    node {
                      mediaContentType
                      alt
                      ... on MediaImage {
                        id
                        image {
                          url
                          altText
                          width
                          height
                        }
                      }
            ... on Video {
              id
              sources {
                url
                mimeType
                format
                height
                width
              }
            }
            ... on ExternalVideo {
              id
              embeddedUrl
            }
                    }
                  }
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                compareAtPriceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                url: onlineStoreUrl
              }
            }
          }
        }
      }
    }
  }
`;

const PRODUCT_FRAGMENT = `
  fragment ProductData on Product {
    id
    title
    handle
    description
    vendor
    type: productType
    created_at: createdAt
    published_at: publishedAt
    available: availableForSale
    tags
    onlineStoreUrl
    options {
      id
      name
      values
    }

    coming_soon_metafield: metafield(namespace: "state", key: "coming_soon") {
      value
    }

    just_dropped_metafield: metafield(namespace: "state", key: "just_dropped") {
      value
    }

    variants(first: 250) {
      edges {
        node {
          id
          title
          price {
            amount
            currencyCode
          }
          compare_at_price: compareAtPrice {
            amount
            currencyCode
          }
          available: availableForSale
          selectedOptions {
            name
            value
          }
          image {
            id
            url
            altText
            width
            height
          }
          featured_image: image {
            id
            url
            altText
            width
            height
          }
          sku
          barcode
          weight
          weight_unit: weightUnit
          requires_shipping: requiresShipping
          taxable
        }
      }
    }

    images(first: 250) {
      edges {
        node {
          id
          url
          altText
          width
          height
        }
      }
    }

    featured_image: featuredImage {
      id
      url
      altText
      width
      height
    }

    featured_media: featuredImage {
      id
      url
      altText
      width
      height
    }

    media(first: 250) {
      edges {
        node {
          mediaContentType
          alt
          ... on MediaImage {
            id
            image {
              url
              altText
              width
              height
            }
          }
          ... on Video {
            id
            sources {
              url
              mimeType
              format
              height
              width
            }
          }
          ... on ExternalVideo {
            id
            embeddedUrl
          }
        }
      }
    }

    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
      maxVariantPrice {
        amount
        currencyCode
      }
    }

    compareAtPriceRange {
      minVariantPrice {
        amount
        currencyCode
      }
      maxVariantPrice {
        amount
        currencyCode
      }
    }

    url: onlineStoreUrl

    siblings: metafield(namespace: "related_products", key: "siblings") {
      value
      type
      references(first: 250) {
        edges {
          node {
            ... on Product {
              id
              title
              handle
              description
              content: description
              vendor
              type: productType
              created_at: createdAt
              published_at: publishedAt
              available: availableForSale
              onlineStoreUrl
              tags
              options {
                id
                name
                values
              }
              variants(first: 250) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    compare_at_price: compareAtPrice {
                      amount
                      currencyCode
                    }
                    available: availableForSale
                    selectedOptions {
                      name
                      value
                    }
                    image {
                      id
                      url
                      altText
                      width
                      height
                    }
                    featured_image: image {
                      id
                      url
                      altText
                      width
                      height
                    }
                    sku
                    barcode
                    weight
                    weight_unit: weightUnit
                    requires_shipping: requiresShipping
                    taxable
                  }
                }
              }
              images(first: 250) {
                edges {
                  node {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
              featured_image: featuredImage {
                id
                url
                altText
                width
                height
              }
              featured_media: featuredImage {
                id
                url
                altText
                width
                height
              }
              media(first: 250) {
                edges {
                  node {
                    mediaContentType
                    alt
                    ... on MediaImage {
                      id
                      image {
                        url
                        altText
                        width
                        height
                      }
                    }
                    ... on Video {
                      id
                      sources {
                        url
                        mimeType
                        format
                        height
                        width
                      }
                    }
                    ... on ExternalVideo {
                      id
                      embeddedUrl
                    }
                  }
                }
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              url: onlineStoreUrl
            }
          }
        }
      }
    }
  }
`;

const PRODUCTS_BULK_QUERY = `
  ${PRODUCT_FRAGMENT}

  query getProductsBulk($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
    nodes(ids: $ids) {
      id
      ... on Product {
        ...ProductData
      }
    }
  }
`;

const PRODUCTS_DYNAMIC_QUERY = `
  query getProductsDynamic($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
    nodes(ids: $ids) {
      id
      ... on Product {
        available: availableForSale
        tags
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        coming_soon_metafield: metafield(namespace: "state", key: "coming_soon") {
          value
        }
        just_dropped_metafield: metafield(namespace: "state", key: "just_dropped") {
          value
        }
        variants(first: 250) {
          edges {
            node {
              id
              price {
                amount
                currencyCode
              }
              compare_at_price: compareAtPrice {
                amount
                currencyCode
              }
              available: availableForSale
            }
          }
        }
        siblings: metafield(namespace: "related_products", key: "siblings") {
          value
          type
          references(first: 25) {
            edges {
              node {
                ... on Product {
                  id
                  available: availableForSale
                  tags
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  compareAtPriceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  variants(first: 250) {
                    edges {
                      node {
                        id
                        price {
                          amount
                          currencyCode
                        }
                        compare_at_price: compareAtPrice {
                          amount
                          currencyCode
                        }
                        available: availableForSale
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Formats a wishlist item ID with optional variant ID
 * @param {string} productId - Product ID
 * @param {string} [variantId] - Optional variant ID
 * @returns {string} Formatted wishlist item ID
 */
const formatWishlistId = (productId, variantId = null) => {
  return variantId
    ? `${productId}:${variantId}`
    : productId;
};

const convertPriceToInteger = (priceString) => {
  if (!priceString || priceString === '0.0') return null;
  return Math.round(parseFloat(priceString) * 100);
};

const convertDateToLiquidFormat = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const timeZone = 'AEST';
  return [
    date.getSeconds(),
    date.getMinutes(),
    date.getHours(),
    date.getDate(),
    date.getMonth() + 1,
    date.getFullYear(),
    date.getDay(),
    Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000),
    false,
    timeZone,
  ];
};

const convertUrlToRelative = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch (e) {
    return url;
  }
};

const convertImageUrlToRelative = (url) => {
  if (!url) return null;

  return url.replace(/^https?:\/\/[^\/]+/, '');
};

const ensureFullImageUrl = (url) => {
  if (!url) return null;

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  if (url.startsWith('/')) {
    return `https://cdn.shopify.com${url}`;
  }
  return url;
};

const transformVariant = (variantData) => {
  const variant = variantData.variant;
  const productTitle = variantData.productTitle;
  const variantId = variant.id
    ? parseInt(variant.id.replace('gid://shopify/ProductVariant/', ''))
    : null;

  return {
    id: variantId,
    title: variant.title,
    option1: variant.selectedOptions?.[0]?.value || null,
    option2: variant.selectedOptions?.[1]?.value || null,
    option3: variant.selectedOptions?.[2]?.value || null,
    sku: variant.sku,
    requires_shipping: variant.requires_shipping,
    taxable: variant.taxable,
    featured_image: null,
    available: variant.available,
    name: `${productTitle} - ${variant.title}`,
    public_title: variant.title,
    options: variant.selectedOptions?.map((opt) => opt.value) || [],
    price: convertPriceToInteger(variant.price?.amount),
    weight: variant.weight
      ? Math.round(variant.weight * 1000)
      : null,
    compare_at_price: convertPriceToInteger(variant.compare_at_price?.amount),
    inventory_management: 'shopify',
    barcode: variant.barcode,
    requires_selling_plan: false,
    selling_plan_allocations: [],
    quantity_rule: {
      min: 1,
      max: null,
      increment: 1,
    },
  };
};

const transformMedia = (mediaNode, position) => {
  const mediaType = mediaNode.mediaContentType?.toLowerCase() || 'image';

  if (mediaType === 'video') {
    const mediaId = mediaNode.id
      ? parseInt(mediaNode.id.replace('gid://shopify/Video/', ''))
      : null;

    let previewImageUrl = null;
    if (mediaNode.sources?.[0]?.url) {
      const videoUrlMatch = mediaNode.sources[0].url.match(/\/([a-f0-9]+)\/[^\/]+\.mp4/);
      if (videoUrlMatch) {
        const videoId = videoUrlMatch[1];
        previewImageUrl = `//whitefoxboutique.com.au/cdn/shop/files/preview_images/${videoId}.thumbnail.0000000000.jpg?v=${Date.now()}`;
      }
    }

    const highestQualitySource =
      mediaNode.sources?.reduce((highest, current) =>
        current.height > (highest?.height || 0)
          ? current
          : highest,
      ) || mediaNode.sources?.[0];

    const aspectRatio =
      highestQualitySource?.width && highestQualitySource?.height
        ? parseFloat((highestQualitySource.width / highestQualitySource.height).toFixed(3))
        : 0.646;

    const transformedSources =
      mediaNode.sources?.map((source) => ({
        format: source.format,
        height: source.height,
        mime_type: source.mimeType,
        url: ensureFullImageUrl(source.url),
        width: source.width,
      })) || [];

    return {
      alt: mediaNode.alt || null,
      id: mediaId,
      position: position,
      preview_image: {
        aspect_ratio: aspectRatio,
        height: 1850,
        width: 1200,
        src: ensureFullImageUrl(previewImageUrl) || ensureFullImageUrl(mediaNode.sources?.[0]?.url),
      },
      aspect_ratio: aspectRatio,
      media_type: 'video',
      sources: transformedSources,
    };
  } else {
    const mediaId = mediaNode.id
      ? parseInt(mediaNode.id.replace('gid://shopify/MediaImage/', ''))
      : null;
    const imageUrl = mediaNode.image?.url || mediaNode.url;
    const aspectRatio =
      mediaNode.image?.width && mediaNode.image?.height
        ? parseFloat((mediaNode.image.width / mediaNode.image.height).toFixed(3))
        : 0.649;

    return {
      alt: mediaNode.alt || null,
      id: mediaId,
      position: position,
      preview_image: {
        aspect_ratio: aspectRatio,
        height: mediaNode.image?.height || mediaNode.height,
        width: mediaNode.image?.width || mediaNode.width,
        src: ensureFullImageUrl(imageUrl),
      },
      aspect_ratio: aspectRatio,
      height: mediaNode.image?.height || mediaNode.height,
      media_type: 'image',
      src: ensureFullImageUrl(imageUrl),
      width: mediaNode.image?.width || mediaNode.width,
    };
  }
};

const convertGraphQLDynamicToLiquidProduct = (product) => {
  const productId = product.id
    ? parseInt(product.id.replace('gid://shopify/Product/', ''))
    : null;

  const variants =
    product.variants?.edges?.map((edge) => ({
      id: edge.node.id
        ? parseInt(edge.node.id.replace('gid://shopify/ProductVariant/', ''))
        : null,
      price: convertPriceToInteger(edge.node.price?.amount),
      compare_at_price: convertPriceToInteger(edge.node.compare_at_price?.amount),
      available: edge.node.available,
    })) || [];

  // Use priceRange from GraphQL for more accurate min/max prices
  const price_min = product.priceRange?.minVariantPrice?.amount
    ? convertPriceToInteger(product.priceRange.minVariantPrice.amount)
    : null;

  const price_max = product.priceRange?.maxVariantPrice?.amount
    ? convertPriceToInteger(product.priceRange.maxVariantPrice.amount)
    : null;

  const compare_at_price_min = product.compareAtPriceRange?.minVariantPrice?.amount
    ? convertPriceToInteger(product.compareAtPriceRange.minVariantPrice.amount)
    : null;

  const compare_at_price_max = product.compareAtPriceRange?.maxVariantPrice?.amount
    ? convertPriceToInteger(product.compareAtPriceRange.maxVariantPrice.amount)
    : null;

  // Fallback to variant calculation if GraphQL ranges are not available
  const validVariantPrices = variants
    .map((v) => v.price)
    .filter((p) => p !== null && p !== undefined);

  const validVariantComparePrices = variants
    .map((v) => v.compare_at_price)
    .filter((p) => p !== null && p !== undefined);

  // Process siblings dynamic data
  const siblings =
    product.siblings?.references?.edges?.map((edge) => {
      const sibling = edge.node;
      const siblingId = sibling.id
        ? parseInt(sibling.id.replace('gid://shopify/Product/', ''))
        : null;

      const siblingVariants =
        sibling.variants?.edges?.map((variantEdge) => ({
          id: variantEdge.node.id
            ? parseInt(variantEdge.node.id.replace('gid://shopify/ProductVariant/', ''))
            : null,
          price: convertPriceToInteger(variantEdge.node.price?.amount),
          compare_at_price: convertPriceToInteger(variantEdge.node.compare_at_price?.amount),
          available: variantEdge.node.available,
        })) || [];

      // Use sibling priceRange from GraphQL for more accurate min/max prices
      const sibling_price_min = sibling.priceRange?.minVariantPrice?.amount
        ? convertPriceToInteger(sibling.priceRange.minVariantPrice.amount)
        : null;

      const sibling_price_max = sibling.priceRange?.maxVariantPrice?.amount
        ? convertPriceToInteger(sibling.priceRange.maxVariantPrice.amount)
        : null;

      const sibling_compare_at_price_min = sibling.compareAtPriceRange?.minVariantPrice?.amount
        ? convertPriceToInteger(sibling.compareAtPriceRange.minVariantPrice.amount)
        : null;

      const sibling_compare_at_price_max = sibling.compareAtPriceRange?.maxVariantPrice?.amount
        ? convertPriceToInteger(sibling.compareAtPriceRange.maxVariantPrice.amount)
        : null;

      // Fallback to variant calculation if GraphQL ranges are not available
      const siblingValidPrices = siblingVariants
        .map((v) => v.price)
        .filter((p) => p !== null && p !== undefined);

      const siblingValidComparePrices = siblingVariants
        .map((v) => v.compare_at_price)
        .filter((p) => p !== null && p !== undefined);

      return {
        id: siblingId,
        available: sibling.available,
        tags: sibling.tags || [],
        variants: siblingVariants,
        price:
          siblingValidPrices.length > 0
            ? siblingValidPrices[0]
            : null,
        price_min:
          sibling_price_min ||
          (siblingValidPrices.length > 0
            ? Math.min(...siblingValidPrices)
            : null),
        price_max:
          sibling_price_max ||
          (siblingValidPrices.length > 0
            ? Math.max(...siblingValidPrices)
            : null),
        compare_at_price:
          siblingValidComparePrices.length > 0
            ? siblingValidComparePrices[0]
            : null,
        compare_at_price_min:
          sibling_compare_at_price_min ||
          (siblingValidComparePrices.length > 0
            ? Math.min(...siblingValidComparePrices)
            : null),
        compare_at_price_max:
          sibling_compare_at_price_max ||
          (siblingValidComparePrices.length > 0
            ? Math.max(...siblingValidComparePrices)
            : null),
      };
    }) || [];

  return {
    id: productId,
    available: product.available,
    tags: product.tags || [],
    variants: variants,
    siblings: siblings,
    price:
      validVariantPrices.length > 0
        ? validVariantPrices[0]
        : null,
    price_min:
      price_min ||
      (validVariantPrices.length > 0
        ? Math.min(...validVariantPrices)
        : null),
    price_max:
      price_max ||
      (validVariantPrices.length > 0
        ? Math.max(...validVariantPrices)
        : null),
    compare_at_price:
      validVariantComparePrices.length > 0
        ? validVariantComparePrices[0]
        : null,
    compare_at_price_min:
      compare_at_price_min ||
      (validVariantComparePrices.length > 0
        ? Math.min(...validVariantComparePrices)
        : null),
    compare_at_price_max:
      compare_at_price_max ||
      (validVariantComparePrices.length > 0
        ? Math.max(...validVariantComparePrices)
        : null),
  };
};

const convertGraphQLProductToLiquidProduct = (product) => {
  const siblings = product.siblings?.references?.edges?.map((edge) => edge.node) || [];
  const productId = product.id
    ? parseInt(product.id.replace('gid://shopify/Product/', ''))
    : null;

  const transformProduct = (prod, isMainProduct = false) => {
    const id = prod.id
      ? parseInt(prod.id.replace('gid://shopify/Product/', ''))
      : null;
    const variants = prod.variants?.edges?.map((edge) => transformVariant({ variant: edge.node, productTitle: prod.title })) || [];
    const images = prod.images?.edges?.map((edge) => ensureFullImageUrl(edge.node.url)) || [];
    const media = prod.media?.edges?.map((edge, index) => transformMedia(edge.node, index + 1)) || [];

    const comingSoonMetafield = prod.coming_soon_metafield?.value && [true, 1, '1', 'true'].includes(prod.coming_soon_metafield?.value);
    const justDroppedMetafield = prod.just_dropped_metafield?.value && [true, 1, '1', 'true'].includes(prod.just_dropped_metafield?.value);

    const minPrice = convertPriceToInteger(prod.priceRange?.minVariantPrice?.amount);
    const maxPrice = convertPriceToInteger(prod.priceRange?.maxVariantPrice?.amount);
    const minComparePrice = convertPriceToInteger(prod.compareAtPriceRange?.minVariantPrice?.amount) || 0;
    const maxComparePrice = convertPriceToInteger(prod.compareAtPriceRange?.maxVariantPrice?.amount) || 0;

    const firstVariant = variants[0] || null;

    return {
      id: id,
      title: prod.title,
      handle: prod.handle,
      description: prod.description,
      content: prod.content || prod.description,
      vendor: prod.vendor,
      type: prod.type,
      created_at: convertDateToLiquidFormat(prod.created_at),
      published_at: convertDateToLiquidFormat(prod.published_at),
      price: minPrice,
      price_min: minPrice,
      price_max: maxPrice,
      price_varies: minPrice !== maxPrice,
      coming_soon_metafield: comingSoonMetafield,
      just_dropped_metafield: justDroppedMetafield,
      compare_at_price:
        minComparePrice > 0
          ? minComparePrice
          : null,
      compare_at_price_min: minComparePrice,
      compare_at_price_max: maxComparePrice,
      compare_at_price_varies: minComparePrice !== maxComparePrice,
      available: prod.available,
      tags: prod.tags || [],
      options: prod.options?.map((option) => option.name) || [],
      options_with_values:
        prod.options?.map((option, index) => ({
          name: option.name,
          position: index + 1,
          values: option.values || [],
        })) || [],
      variants: variants,
      variants_count: variants.length,
      images: images,
      featured_image: images[0] || null,
      featured_media: media[0] || null,
      media: media,
      url: convertUrlToRelative(prod.url),
      has_only_default_variant: variants.length <= 1,
      requires_selling_plan: false,
      selling_plan_groups: [],
      selected_variant: isMainProduct
        ? firstVariant
        : null,
      selected_or_first_available_variant: firstVariant,
      first_available_variant: firstVariant,
      template_suffix: null,
      online_store_url: prod.onlineStoreUrl,
    };
  };

  const mainProduct = transformProduct(product, true);
  const siblingProducts = siblings.map((sibling) => transformProduct(sibling, false));

  return {
    product: mainProduct,
    siblings: siblingProducts,
  };
};

/**
 * Parses a wishlist item ID to extract product and variant IDs
 * @param {string} wishlistId - Wishlist item ID (productId or productId:variantId)
 * @returns {Object} Object with productId and variantId
 */
const parseWishlistId = (wishlistId) => {
  const parts = wishlistId?.split(':');
  return {
    productId: parts[0],
    variantId:
      parts.length > 1
        ? parts[1]
        : null,
  };
};

/**
 * Helper functions for working with allProductIds object
 */
const productIdsHelper = {
  /**
   * Adds a product ID to the allProductIds object
   * @param {Object} allProductIds - The allProductIds object
   * @param {string} productId - Product ID to add
   */
  add: (allProductIds, productId) => {
    allProductIds[productId] = true;
  },

  /**
   * Removes a product ID from the allProductIds object
   * @param {Object} allProductIds - The allProductIds object
   * @param {string} productId - Product ID to remove
   */
  remove: (allProductIds, productId) => {
    delete allProductIds[productId];
  },

  /**
   * Checks if a product ID exists in the allProductIds object
   * @param {Object} allProductIds - The allProductIds object
   * @param {string} productId - Product ID to check
   * @returns {boolean} True if product exists
   */
  has: (allProductIds, productId) => {
    return !!allProductIds[productId];
  },

  /**
   * Gets all product IDs as an array
   * @param {Object} allProductIds - The allProductIds object
   * @returns {string[]} Array of product IDs
   */
  toArray: (allProductIds) => {
    return Object.keys(allProductIds);
  },

  /**
   * Gets the count of product IDs
   * @param {Object} allProductIds - The allProductIds object
   * @returns {number} Count of product IDs
   */
  count: (allProductIds) => {
    return Object.keys(allProductIds).length;
  },

  /**
   * Converts an array of product IDs to the allProductIds object format
   * @param {string[]} productIdArray - Array of product IDs
   * @returns {Object} allProductIds object
   */
  fromArray: (productIdArray) => {
    const obj = {};
    productIdArray.forEach((id) => {
      obj[id] = true;
    });
    return obj;
  },
};

/**
 * Calculates the total number of items across all boards (including duplicates)
 * @param {Array} boards - Array of board objects
 * @returns {number} Total count of items across all boards
 */
const calculateTotalItemCount = (boards) => {
  return boards.reduce((total, board) => {
    return (
      total +
      (board.items
        ? board.items.length
        : 0)
    );
  }, 0);
};

/**
 * Separates product IDs into those with ongoing queries vs those needing new queries
 * @param {string[]} productIds - Array of product IDs to check
 * @returns {Object} Object with reusablePromises Map and newProductIds array
 */
const separateQueriesFromOngoing = (productIds) => {
  const reusablePromises = new Map();
  const newProductIds = [];

  for (const productId of productIds) {
    if (ongoingProductQueries.has(productId)) {
      reusablePromises.set(productId, ongoingProductQueries.get(productId));
    } else {
      newProductIds.push(productId);
    }
  }

  return { reusablePromises, newProductIds };
};

/**
 * Refreshes dynamic product data for specified product IDs
 * @param {string[]} productIds - Array of product IDs to refresh
 * @returns {Promise<void>}
 */
const refreshDynamicProductData = async (productIds) => {
  if (productIds.length === 0) return;

  const cache = productCache();
  const { country } = getCurrentMarketContext();

  try {
    const batches = [];
    for (let i = 0; i < productIds.length; i += 50) {
      batches.push(productIds.slice(i, i + 50));
    }

    for (const batch of batches) {
      const productGids = batch.map((id) => `gid://shopify/Product/${id}`);

      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({
          query: PRODUCTS_DYNAMIC_QUERY,
          variables: {
            ids: productGids,
            country,
          },
        }),
      });

      if (response.ok) {
        const fetchedData = await response.json();

        if (fetchedData.data?.nodes) {
          for (const product of fetchedData.data.nodes) {
            if (product && product.id) {
              const productId = product.id.replace('gid://shopify/Product/', '');
              const dynamicData = convertGraphQLDynamicToLiquidProduct(product);

              const existingCached = await cache.get(productId);

              if (existingCached) {
                // Use cache's built-in dynamic update - it will handle merging automatically
                // The dynamicData already contains the product with siblings, so pass it as-is
                await cache.set(productId, dynamicData, 'dynamic');
              } else {
                // Fallback: cache as full product if not found
                await cache.set(productId, dynamicData, 'full');
              }
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('Error refreshing dynamic product data:', error);
  }
};

/**
 * Gets product data for all items in the wishlist with cached product information using bulk GraphQL queries
 * @param {number} [boardId] - Optional board ID to get products for specific board
 * @returns {Promise<Array>} Array of objects containing product, siblings, and selectedVariant
 */
const getProducts = async (boardId = null) => {
  // Request deduplication wrapper
  const requestKey = `${boardId || 'all'}_v${wishlistDataVersion}`;

  if (ongoingRequests.has(requestKey)) {
    return await ongoingRequests.get(requestKey);
  }

  // Wrap the existing implementation
  const requestPromise = (async () => {
    const wishlist = await getWishlist();

    if (!wishlist || !wishlist.boards) {
      return [];
    }

    const cache = productCache();
    const { country } = getCurrentMarketContext();
    let itemsToProcess = [];

    if (boardId) {
      const board = wishlist.boards.find((b) => b.id === boardId);
      if (board && board.items) {
        itemsToProcess = board.items;
      }
    } else {
      itemsToProcess = productIdsHelper.toArray(wishlist.allProductIds || {});
    }

    if (itemsToProcess.length === 0) {
      return [];
    }

    const cachedProducts = new Map();
    const uncachedProductIds = [];
    const dynamicRefreshProductIds = [];

    for (const itemId of itemsToProcess) {
      const { productId } = parseWishlistId(itemId);
      const cachedData = await cache.get(productId);
      const updateNeeds = cachedData
        ? cache.needsUpdate(cachedData)
        : { full: true };

      if (cachedData && !updateNeeds.full) {
        cachedProducts.set(productId, cachedData);

        // If only dynamic data needs update, add to dynamic refresh list
        if (updateNeeds.dynamicOnly) {
          dynamicRefreshProductIds.push(productId);
        }
      } else {
        uncachedProductIds.push(productId);
      }
    }

    const fetchedProducts = new Map();

    if (uncachedProductIds.length > 0) {
      const { reusablePromises, newProductIds } = separateQueriesFromOngoing(uncachedProductIds);

      for (const [productId, promise] of reusablePromises) {
        try {
          const result = await promise;
          if (result) {
            fetchedProducts.set(productId, result);
          }
        } catch (error) {
          console.error(`Error waiting for ongoing query for product ${productId}:`, error);
        }
      }

      if (newProductIds.length > 0) {
        const batches = [];
        for (let i = 0; i < newProductIds.length; i += 20) {
          batches.push(newProductIds.slice(i, i + 20));
        }

        const unpublishedProductIds = [];

        for (const batch of batches) {
          const batchPromise = (async () => {
            try {
              const productIds = batch.map((id) => `gid://shopify/Product/${id}`);

              const response = await fetch(STOREFRONT_API_URL, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
                },
                body: JSON.stringify({
                  query: PRODUCTS_BULK_QUERY,
                  variables: {
                    ids: productIds,
                    country,
                  },
                }),
              });

              if (!response.ok) {
                console.warn(`Failed to fetch batch: ${response.status} ${response.statusText}`);
                return new Map();
              }

              const fetchedData = await response.json();

              if (fetchedData.errors) {
                console.warn('GraphQL errors in bulk fetch:', fetchedData.errors);
              }

              const batchResults = new Map();

              if (fetchedData.data?.nodes) {
                const requestedIds = new Set(batch);
                
                for (const product of fetchedData.data.nodes) {
                  if (!product) {
                    continue;
                  }
                  
                  if (product.id) {
                    const productId = product.id.replace('gid://shopify/Product/', '');
                    
                    requestedIds.delete(productId);

                    if (!product.onlineStoreUrl) {
                      unpublishedProductIds.push(productId);
                      continue;
                    }

                    const convertedProductData = convertGraphQLProductToLiquidProduct(product);
                    batchResults.set(productId, convertedProductData);

                    await cache.set(productId, convertedProductData, 'full');
                  }
                }
                
                for (const missingProductId of requestedIds) {
                  unpublishedProductIds.push(missingProductId);
                }
              }

              return batchResults;
            } catch (error) {
              console.error('Error in bulk fetch batch:', error);
              return new Map();
            }
          })();

          batch.forEach((productId) => {
            const productPromise = batchPromise.then((batchResults) => batchResults.get(productId));
            ongoingProductQueries.set(productId, productPromise);
          });

          try {
            const batchResults = await batchPromise;
            for (const [productId, result] of batchResults) {
              fetchedProducts.set(productId, result);
            }
          } catch (error) {
            console.error('Error in batch processing:', error);
          } finally {
            batch.forEach((productId) => {
              ongoingProductQueries.delete(productId);
            });
          }
        }


        if (unpublishedProductIds.length > 0) {
          await removeFromAllBoardsMultiple(unpublishedProductIds);
        }
      }
    }

    // Trigger dynamic refresh for products that need it
    if (dynamicRefreshProductIds.length > 0) {
      refreshDynamicProductData(dynamicRefreshProductIds).catch((error) => {
        console.error('Error refreshing dynamic product data:', error);
      });
    }

    const results = [];

    for (const itemId of itemsToProcess) {
      try {
        const { productId, variantId } = parseWishlistId(itemId);

        const productData = cachedProducts.get(productId) || fetchedProducts.get(productId);

        if (!productData) {
          continue; // Skip products we couldn't fetch
        }

        const product = productData.product;
        const siblings = productData.siblings || [];

        if (!product) continue;

        if (!product.url) {
          console.warn(`Product ${productId} has no URL after conversion, excluding from wishlist`);
          continue;
        }

        const variants = product.variants || [];

        // Apply same variant selection logic as before
        let selectedVariant = null;
        if (variantId) {
          selectedVariant = variants.find((variant) => String(variant.id) === String(variantId)) || null;
        } else {
          const wishlistProductData = wishlist.productData?.[productId];
          if (wishlistProductData?.selectedVariantId) {
            selectedVariant = variants.find((variant) => String(variant.id) === String(wishlistProductData.selectedVariantId)) || null;
          }
        }

        results.push({
          product,
          siblings,
          selectedVariant,
          itemId,
        });
      } catch (error) {
        console.error('Error processing product:', error);
        continue;
      }
    }

    return results;
  })();

  ongoingRequests.set(requestKey, requestPromise);

  try {
    return await requestPromise;
  } finally {
    ongoingRequests.delete(requestKey);
  }
};

/**
 * Gets the current wishlist from IndexedDB and ensures default board exists
 * @returns {Promise<Object>} The wishlist object with guaranteed default board
 */
const getWishlist = async () => {
  try {
    const wishlist = await getItem(STORES.WISHLIST, 'wishlist');
    const validWishlist = wishlist || DEFAULT_WISHLIST;
    return ensureDefaultBoard(validWishlist);
  } catch (error) {
    return ensureDefaultBoard(DEFAULT_WISHLIST);
  }
};

/**
 * Gets the share URL for the current wishlist
 * @returns {Promise<string|undefined>} Share URL or undefined if not available
 */
const getShareUrl = async (boardId = BOARD_CONFIG.DEFAULT_BOARD_ID) => {
  if (!window.Auth?.wishlist?.share) {
    return;
  }

  const shareUrl = `${window.location.origin}${window.Shopify.routes.root}pages/share-wishlist?&wishlist_id=${window.Auth.wishlist.share}&board_id=${boardId}`;
  return shareUrl;
};

/**
 * Saves the wishlist to IndexedDB
 * @param {Object} wishlist - The wishlist to save
 * @returns {Promise<void>}
 */
const saveWishlist = async (wishlist) => {
  try {
    window.wishlist.boards = wishlist.boards;
    const wishlistToSave = { ...wishlist, id: 'wishlist' };

    await putItem(STORES.WISHLIST, wishlistToSave);
  } catch (error) { }
};

/**
 * Sends a single wishlist operation to backend
 * @param {string} operation - The operation type ('add' or 'remove')
 * @param {string[]} productIds - Array of product IDs
 * @param {Object} options - Optional data including variant IDs
 * @returns {void}
 */
/**
 * Sends a unified wishlist operation to backend using new API structure
 * @param {string} operation - The operation type ('add', 'remove', 'updateVariant', 'createBoard', 'deleteBoard', 'renameBoard', 'moveProduct')
 * @param {Object} payload - Operation-specific payload
 * @returns {Promise<boolean>} True if request was sent successfully
 */
const sendWishlistUpdate = async (operation, payload) => {
  if (!canSendWishlistOperations()) return false;

  const endpoint =
    operation === 'get'
      ? WISHLIST_GET_ENDPOINT
      : WISHLIST_OPERATIONS_ENDPOINT;

  let requestPayload;

  if (operation === 'add' || operation === 'remove' || operation === 'updateVariant') {
    // Handle both single item and multiple items
    let items;
    if (payload.items && Array.isArray(payload.items)) {
      // Multiple items format
      items = payload.items;
    } else if (payload.productId) {
      // Single item format (backward compatibility)
      items = [
        {
          productId: payload.productId,
          variantId: payload.variantId,
        },
      ];
    } else {
      throw new Error('Either items array or productId is required');
    }

    requestPayload = {
      config: window.region_code,
      operation,
      items: items,
      ...(operation !== 'updateVariant' && { boardId: payload.boardId || 1 }),
      boardName: payload.boardName,
    };
  } else if (operation === 'removeAllItems') {
    requestPayload = {
      config: window.region_code,
      operation,
      items: payload.items,
    };
  } else if (operation === 'moveProduct') {
    // Handle moveProduct operation with new items array structure
    requestPayload = {
      config: window.region_code,
      operation,
      ...payload, // This will include items, fromBoardId, toBoardId
    };

    // Support backward compatibility for single productId
    if (payload.productId && !payload.items) {
      requestPayload.productId = payload.productId;
    }
  } else {
    requestPayload = {
      config: window.region_code,
      operation,
      ...payload,
    };
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      keepalive: true,
      body: JSON.stringify(requestPayload),
      headers: {
        'Content-Type': 'application/json',
        'x-wf-token': window.Auth.wishlist.token,
        'x-wf-value': window.Auth.wishlist.value,
      },
    });

    if (response.ok) {
      const result = await response.json();
      return result.success === true;
    }

    return false;
  } catch (error) {
    console.error('Wishlist update failed:', error);
    return false;
  }
};

/**
 * Validates if we can send wishlist operations
 * @returns {boolean} True if all required auth is available
 */
const canSendWishlistOperations = () => {
  return !!(window.Shopify.customer_id && window.Auth.wishlist?.token && window.Auth.wishlist?.value);
};

/**
 * Sends bulk wishlist operations to backend with individual operation tracking
 * Groups operations by type and sends them in optimized batches to match server API
 * @param {Array<{id: string, operation: string, productId: string, variantId?: string, boardId?: number}>} operations - Array of operations with IDs
 * @returns {Promise<{successful: string[], failed: string[]}>} Object containing successful and failed operation IDs
 */
const sendBulkWishlistOperations = async (operations) => {
  if (!canSendWishlistOperations()) {
    return { successful: [], failed: operations.map((op) => op.id) };
  }
  if (!operations?.length) {
    return { successful: [], failed: [] };
  }

  const successful = [];
  const failed = [];

  try {
    const operationGroups = operations.reduce((groups, op) => {
      // For removeAllItems, don't group by boardId since it affects all boards
      const key =
        op.operation === 'removeAllItems'
          ? op.operation
          : `${op.operation}_${op.boardId || 1}`;
      if (!groups[key]) {
        groups[key] = {
          operation: op.operation,
          boardId:
            op.operation === 'removeAllItems'
              ? undefined
              : op.boardId || 1,
          items: [],
          operationIds: [],
        };
      }

      groups[key].items.push({
        productId: op.productId,
        variantId: op.variantId,
      });

      groups[key].operationIds.push(op.id);

      return groups;
    }, {});

    for (const group of Object.values(operationGroups)) {
      try {
        const payload = {
          config: window.region_code,
          operation: group.operation,
          items: group.items,
        };

        // Only add boardId for operations that need it (not removeAllItems)
        if (group.boardId !== undefined) {
          payload.boardId = group.boardId;
        }

        const response = await fetch(WISHLIST_OPERATIONS_ENDPOINT, {
          method: 'POST',
          keepalive: true,
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json',
            'x-wf-token': window.Auth.wishlist.token,
            'x-wf-value': window.Auth.wishlist.value,
          },
        });

        if (response.ok) {
          const result = await response.json();

          if (result.success) {
            successful.push(...group.operationIds);
          } else {
            failed.push(...group.operationIds);
            console.warn(`Failed to process ${group.operation} operation: ${result.message || 'Unknown error'}`);
          }
        } else {
          failed.push(...group.operationIds);
          console.error(`HTTP error for ${group.operation} operation: ${response.status} ${response.statusText}`);
        }
      } catch (groupError) {
        console.error(`Error processing ${group.operation} group:`, groupError);
        failed.push(...group.operationIds);
      }
    }
  } catch (error) {
    console.error('Bulk wishlist operations failed:', error);

    operations.forEach((op) => failed.push(op.id));
  }

  if (successful.length > 0) {
    cleanupSuccessfulOperations(successful);
  }

  if (failed.length > 0) {
    console.warn(`${failed.length} operations failed and will be retried later`);
  }

  return { successful, failed };
};

/**
 * Sends batch wishlist operations to backend using bulk API
 * @param {Array<{operation: string, productId: string, variantId?: string, boardId?: number}>} items - Array of items to process
 * @returns {Promise<boolean>} True if request was sent successfully
 */
const sendWishlistBeacons = async (items) => {
  if (!canSendWishlistOperations()) return false;
  if (!items?.length) return true;

  try {
    const operations = items.map((item, index) => ({
      id: `temp_${Date.now()}_${index}`,
      operation: item.operation,
      productId: item.productId,
      variantId: item.variantId,
      boardId: item.boardId || 1,
    }));

    const result = await sendBulkWishlistOperations(operations);
    return result.successful.length > 0;
  } catch (error) {
    console.error('Batch wishlist operations failed:', error);
    return false;
  }
};

/**
 * Converts local wishlist changes to pending operations and triggers immediate bulk sync when user logs in
 * Creates individual operations that get batched automatically by sendBulkWishlistOperations
 * @param {Object} localWishlist - The local wishlist to convert
 * @returns {Promise<void>}
 */
const convertLocalChangesToPendingOperations = async (localWishlist) => {
  if (!localWishlist.allProductIds || productIdsHelper.count(localWishlist.allProductIds) === 0) {
    return;
  }

  const operations = [];
  const localProducts = productIdsHelper.toArray(localWishlist.allProductIds);

  localProducts.forEach((productId) => {
    const productData = localWishlist.productData?.[productId];
    if (!productData) return;

    const addOpId = addPendingOperation('add', productId, productData.productId, productData.selectedVariantId);
    operations.push({
      id: addOpId,
      operation: 'add',
      productId: productData.productId,
      variantId: productData.selectedVariantId,
      boardId: 1,
    });

    if (productData.selectedVariantId) {
      const updateOpId = addPendingOperation('updateVariant', productId, productData.productId, productData.selectedVariantId);
      operations.push({
        id: updateOpId,
        operation: 'updateVariant',
        productId: productData.productId,
        variantId: productData.selectedVariantId,
        boardId: 1,
      });
    }
  });

  if (operations.length > 0) {
    const result = await sendBulkWishlistOperations(operations);
  }
};

/**
 * Adds a product to the wishlist
 * @param {string} productId - ID of product to add
 * @param {string|Object} [boardNameOrOptions=BOARD_CONFIG.DEFAULT_BOARD_NAME] - Board name string or options object
 * @param {string} [variantId] - Optional variantId if already known (when using string boardName)
 * @fires wishlist:add
 * @returns {Promise<Object>} Updated wishlist
 *
 * Usage:
 * - add(productId) - adds to default board
 * - add(productId, "Board Name") - adds to named board
 * - add(productId, "Board Name", variantId) - adds to named board with variant
 * - add(productId, { boardName: "Board", boardId: 1, variantId: "123" }) - adds with full options
 */
const add = async (productId, boardId = BOARD_CONFIG.DEFAULT_BOARD_ID, variantId = null, insertAfterProductId = null, cardId = null) => {
  if (!productId) {
    throw new Error('productId is required');
  }

  if (typeof productId !== 'string') {
    throw new Error('productId must be a string');
  }

  const currentWishlist = await getWishlist();

  const selectedVariantId = variantId;

  const targetBoard = currentWishlist.boards.find((board) => board.id === boardId);
  if (!targetBoard) {
    throw new Error('Board not found');
  }

  const boardName = targetBoard?.name;

  const productExistsInTargetBoard = targetBoard?.items?.some((item) => {
    const { productId: itemProductId } = parseWishlistId(item);
    return itemProductId === productId;
  });

  if (productExistsInTargetBoard) {
    const currentProductData = currentWishlist.productData?.[productId];
    const currentVariantId = currentProductData?.selectedVariantId;

    if (selectedVariantId && currentVariantId !== selectedVariantId) {
      return await updateVariant(productId, selectedVariantId, targetBoard.id);
    } else {
      return currentWishlist;
    }
  }

  try {
    if (isLoggedIn()) {
      addPendingOperation(OPERATION_TYPES.ADD, productId, productId, selectedVariantId, { boardId, boardName });
    }

    const minimalProductData = {
      productId: productId,
      selectedVariantId: selectedVariantId,
    };

    const updatedWishlist = await handleAddJSON(productId, targetBoard, minimalProductData, insertAfterProductId);

    const addEvent = new CustomEvent('wishlist:add', {
      detail: {
        ...updatedWishlist,
        productId: productId,
        cardId: cardId || `card-${productId}`, // Use passed cardId or fallback to default format
      },
    });
    document.dispatchEvent(addEvent);

    invalidateCache();

    if (isLoggedIn()) {
      sendWishlistUpdate(OPERATION_TYPES.ADD, {
        productId: productId,
        variantId: selectedVariantId,
        boardName: boardName,
        boardId: boardId,
        insertAfterProductId: insertAfterProductId,
      }).catch(() => { });
    }

    const updateEvent = new CustomEvent('wishlist:update', {
      detail: {
        ...updatedWishlist,
      },
    });
    document.dispatchEvent(updateEvent);

    return updatedWishlist;
  } catch (error) {
    console.error('Error adding to wishlist:', error);

    // Dispatch operation failed event for context cleanup
    const failedEvent = new CustomEvent('wishlist:operation-failed', {
      detail: {
        productId: productId,
        cardId: cardId || `card-${productId}`,
        error: error.message,
      },
    });
    document.dispatchEvent(failedEvent);

    throw error;
  }
};

/**
 * Helper function to add a product to the wishlist JSON
 * @param {string} productId - ID of product to add
 * @param {string} [boardName=BOARD_CONFIG.DEFAULT_BOARD_NAME] - Name of board to add product to
 * @param {Object} productData - Product data with productId and selectedVariantId
 * @returns {Promise<Object>} Updated wishlist
 * @private
 */
const handleAddJSON = async (productId, targetBoard, productData = null, insertAfterProductId = null) => {
  if (!productId || !productData || !targetBoard) {
    throw new Error('Error adding local product to wishlist');
  }

  const currentWishlist = await getWishlist();

  if (!productIdsHelper.has(currentWishlist.allProductIds, productId)) {
    productIdsHelper.add(currentWishlist.allProductIds, productId);
  }

  if (!currentWishlist.productData) {
    currentWishlist.productData = {};
  }
  currentWishlist.productData[productId] = {
    productId: productData.productId,
    selectedVariantId: productData.selectedVariantId,
  };

  const updatedBoards = currentWishlist.boards.map((board) => {
    if (board.name === targetBoard.name) {
      if (insertAfterProductId) {
        const insertAfterIndex = board.items.findIndex(item => item === String(insertAfterProductId));
        if (insertAfterIndex !== -1) {
          const newItems = [...board.items];
          newItems.splice(insertAfterIndex + 1, 0, productId);
          return { ...board, items: newItems };
        }
      }
      return { ...board, items: [...board.items, productId] };
    }
    return board;
  });

  currentWishlist.count = calculateTotalItemCount(updatedBoards);
  currentWishlist.boards = updatedBoards;

  await saveWishlist(currentWishlist);

  return currentWishlist;
};

/**
 * Removes one or more products from a specific board in the wishlist
 * @param {string|Array<string>} productIdOrIds - Single product ID string or array of product IDs to remove
 * @param {number} boardId - ID of board to remove product(s) from
 * @fires wishlist:remove
 * @returns {Promise<Object>} Updated wishlist
 */
const remove = async (productIdOrIds, boardId = BOARD_CONFIG.DEFAULT_BOARD_ID) => {
  if (!productIdOrIds) {
    throw new Error('Product ID(s) are required');
  }

  if (!isValidBoardId(boardId)) {
    throw new Error(`Board ID must be a valid number between 1 and ${BOARD_CONFIG.MAX_BOARDS}`);
  }

  let productIds;
  if (Array.isArray(productIdOrIds)) {
    productIds = productIdOrIds;
  } else if (typeof productIdOrIds === 'string') {
    productIds = [productIdOrIds];
  } else {
    throw new Error('First parameter must be either a productId string or array of productIds');
  }

  if (productIds.length === 0) {
    throw new Error('At least one product ID is required');
  }

  const currentWishlist = await getWishlist();

  const itemsToRemove = [];
  productIds.forEach((productId) => {
    const productData = currentWishlist.productData?.[productId];
    const selectedVariantId = productData?.selectedVariantId || null;
    itemsToRemove.push({
      productId,
      selectedVariantId,
    });
  });

  let updatedWishlist;
  let removedCount = 0;

  if (productIds.length === 1) {
    updatedWishlist = await handleRemoveJSON(productIds[0], boardId);
    removedCount = 1;
  } else {
    const beforeCount = currentWishlist.count;
    updatedWishlist = await handleRemoveMultipleJSON(productIds, boardId);
    removedCount = beforeCount - updatedWishlist.count;
  }

  if (isLoggedIn()) {
    if (productIds.length === 1) {
      const item = itemsToRemove[0];
      addPendingOperation(OPERATION_TYPES.REMOVE, item.productId, item.productId, item.selectedVariantId, { boardId });

      await sendWishlistUpdate(OPERATION_TYPES.REMOVE, {
        productId: item.productId,
        variantId: item.selectedVariantId,
        boardId: boardId,
      });
    } else {
      const items = itemsToRemove.map((item) => ({
        productId: item.productId,
        variantId: item.selectedVariantId,
      }));

      itemsToRemove.forEach((item) => {
        addPendingOperation(OPERATION_TYPES.REMOVE, item.productId, item.productId, item.selectedVariantId, { boardId });
      });

      await sendWishlistUpdate(OPERATION_TYPES.REMOVE, {
        items: items,
        boardId: boardId,
      });
    }
  }

  const eventDetail = {
    removedCount,
    ...updatedWishlist,
  };

  if (productIds.length === 1) {
    eventDetail.productId = productIds[0];
    eventDetail.variantId = itemsToRemove[0].selectedVariantId;
  } else {
    eventDetail.productIds = productIds;
    eventDetail.items = itemsToRemove;
  }

  const removeEvent = new CustomEvent('wishlist:remove', {
    detail: eventDetail,
  });
  document.dispatchEvent(removeEvent);

  invalidateCache();

  const updateEvent = new CustomEvent('wishlist:update', {
    detail: {
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(updateEvent);

  return updatedWishlist;
};

/**
 * Helper function to remove multiple products from a specific board in the wishlist JSON
 * @param {string[]} productIds - Array of product IDs to remove
 * @param {number} boardId - ID of board to remove products from
 * @returns {Promise<Object>} Updated wishlist
 * @private
 */
const handleRemoveMultipleJSON = async (productIds, boardId) => {
  if (!productIds || !Array.isArray(productIds) || productIds.length === 0 || !isValidBoardId(boardId)) {
    return await getWishlist();
  }

  const currentWishlist = await getWishlist();

  const targetBoard = currentWishlist.boards.find((board) => board.id === boardId);
  if (!targetBoard) {
    throw new Error(`Board with ID ${boardId} not found`);
  }

  const itemsToRemove = targetBoard.items?.filter((item) => {
    const { productId: itemProductId } = parseWishlistId(item);
    return productIds.includes(itemProductId);
  }) || [];

  if (itemsToRemove.length === 0) {
    console.warn(`No products found in board ${boardId} for removal`);
    return currentWishlist;
  }

  targetBoard.items = targetBoard.items.filter((item) => !itemsToRemove.includes(item));

  const productsToRemoveFromGlobal = [];
  for (const productId of productIds) {
    const productInOtherBoards = currentWishlist.boards.some(
      (board) =>
        board.id !== boardId &&
        board.items?.some((item) => {
          const { productId: itemProductId } = parseWishlistId(item);
          return itemProductId === productId;
        }),
    );

    if (!productInOtherBoards) {
      productsToRemoveFromGlobal.push(productId);
    }
  }

  for (const productId of productsToRemoveFromGlobal) {
    productIdsHelper.remove(currentWishlist.allProductIds, productId);
    if (currentWishlist.productData && currentWishlist.productData[productId]) {
      delete currentWishlist.productData[productId];
    }
  }

  currentWishlist.count = calculateTotalItemCount(currentWishlist.boards);
  await saveWishlist(currentWishlist);

  return currentWishlist;
};

/**
 * Helper function to remove a product from a specific board in the wishlist JSON
 * @param {string} productId - ID of product to remove
 * @param {number} boardId - ID of board to remove product from
 * @returns {Promise<Object>} Updated wishlist
 * @private
 */
const handleRemoveJSON = async (productId, boardId) => {
  if (!productId || !isValidBoardId(boardId)) return;

  const currentWishlist = await getWishlist();

  const targetBoard = currentWishlist.boards.find((board) => board.id === boardId);
  if (!targetBoard) {
    throw new Error(`Board with ID ${boardId} not found`);
    return currentWishlist;
  }

  const itemToRemove = targetBoard.items?.find((item) => {
    const { productId: itemProductId } = parseWishlistId(item);
    return itemProductId === productId;
  });

  if (!itemToRemove) {
    console.warn(`Product ${productId} not found in board ${boardId}`);
    return currentWishlist;
  }

  targetBoard.items = targetBoard.items.filter((item) => item !== itemToRemove);

  const productInOtherBoards = currentWishlist.boards.some(
    (board) =>
      board.id !== boardId &&
      board.items?.some((item) => {
        const { productId: itemProductId } = parseWishlistId(item);
        return itemProductId === productId;
      }),
  );

  if (!productInOtherBoards) {
    productIdsHelper.remove(currentWishlist.allProductIds, productId);
    if (currentWishlist.productData && currentWishlist.productData[productId]) {
      delete currentWishlist.productData[productId];
    }
  }

  currentWishlist.count = calculateTotalItemCount(currentWishlist.boards);
  await saveWishlist(currentWishlist);

  return currentWishlist;
};

/**
 * Updates the selected variant for a product in a specific board
 * @param {string} productId - ID of product to update
 * @param {string} variantId - New variant ID to select
 * @param {number} boardId - ID of board where product variant should be updated
 * @fires wishlist:variant-updated
 * @returns {Promise<Object>} Updated wishlist
 */
const updateVariant = async (productId, variantId, boardId = null) => {
  if (!productId || !variantId) {
    console.error('updateVariant(): productId and variantId are required');
    return;
  }

  // TODO: add this back in if we require boards specific updates
  // if (!isValidBoardId(boardId)) {
  //   console.error(`updateVariant(): boardId must be a valid number between 1 and ${BOARD_CONFIG.MAX_BOARDS}`);
  //   return;
  // }

  const currentWishlist = await getWishlist();

  if (!productIdsHelper.has(currentWishlist.allProductIds, productId)) {
    return currentWishlist;
  }

  if (!currentWishlist.productData) {
    currentWishlist.productData = {};
  }

  if (currentWishlist.productData[productId]) {
    currentWishlist.productData[productId].selectedVariantId = String(variantId);
  } else {
    currentWishlist.productData[productId] = {
      productId: productId,
      selectedVariantId: String(variantId),
    };
  }

  await saveWishlist(currentWishlist);

  if (isLoggedIn()) {
    const productData = currentWishlist.productData[productId];
    addPendingOperation(OPERATION_TYPES.UPDATE_VARIANT, productId, productData.productId, productData.selectedVariantId, { boardId });

    await sendWishlistUpdate(OPERATION_TYPES.UPDATE_VARIANT, {
      productId: productData.productId,
      variantId: productData.selectedVariantId,
      boardId: boardId,
    });
  }

  const updateEvent = new CustomEvent('wishlist:variant-updated', {
    detail: {
      productId,
      variantId: String(variantId),
      ...currentWishlist,
    },
  });
  document.dispatchEvent(updateEvent);

  invalidateCache();

  return currentWishlist;
};

/**
 * Retrieves the current wishlist
 * @returns {Promise<Object>} Current wishlist
 */
const retrieve = async () => {
  return await getWishlist();
};

/**
 * Deletes a specific board from the wishlist
 * @param {number} boardId - ID of the board to delete
 * @fires wishlist:delete-board
 * @returns {Promise<Object>} Updated wishlist
 * @throws {Error} If attempting to delete the default board
 */
const deleteBoard = async (boardId) => {
  if (!isValidBoardId(boardId)) {
    throw new Error(`deleteBoard: boardId must be a valid number between 1 and ${BOARD_CONFIG.MAX_BOARDS}`);
  }

  if (boardId === BOARD_CONFIG.DEFAULT_BOARD_ID) {
    throw new Error(`Cannot delete the default board (ID ${BOARD_CONFIG.DEFAULT_BOARD_ID}). The default board is required and cannot be removed.`);
  }

  const wishlist = await getWishlist();

  const boardToDelete = wishlist.boards.find((board) => board.id === boardId);
  if (!boardToDelete) {
    console.warn(`Board with ID ${boardId} not found`);
    return wishlist;
  }

  const updatedBoards = wishlist.boards.filter((board) => board.id !== boardId);

  const allProductIdsSet = new Set();
  updatedBoards.forEach((board) => {
    if (board.items && board.active) {
      board.items.forEach((item) => {
        const { productId } = parseWishlistId(item);
        allProductIdsSet.add(productId);
      });
    }
  });

  const updatedWishlist = {
    ...wishlist,
    boards: updatedBoards,
    allProductIds: productIdsHelper.fromArray(Array.from(allProductIdsSet)),
    count: calculateTotalItemCount(updatedBoards),
  };

  await saveWishlist(updatedWishlist);

  if (isLoggedIn()) {
    addPendingOperation(OPERATION_TYPES.DELETE_BOARD, `board_${boardId}`, boardId, null, {
      boardId: boardId,
      boardName: boardToDelete.name,
    });

    await sendWishlistUpdate(OPERATION_TYPES.DELETE_BOARD, {
      boardId: boardId,
    });
  }

  const deleteBoardEvent = new CustomEvent('wishlist:delete-board', {
    detail: {
      boardId,
      boardName: boardToDelete.name,
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(deleteBoardEvent);

  invalidateCache();

  const updateEvent = new CustomEvent('wishlist:update', {
    detail: {
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(updateEvent);

  return updatedWishlist;
};

/**
 * Creates a new board in the wishlist
 * @param {string} boardName - Name of the new board to create
 * @param {string} [colour='#FF6B6B'] - Colour for the board
 * @param {string} [emoji='📋'] - Emoji for the board
 * @fires wishlist:create-board
 * @returns {Promise<Object>} Updated wishlist
 */
const createBoard = async (boardName, colour = BOARD_CONFIG.DEFAULT_BOARD_COLOUR, emoji = BOARD_CONFIG.DEFAULT_BOARD_EMOJI) => {
  if (!boardName) {
    throw new Error('Board name is required');
  }

  if (isSuspicious(boardName)) {
    throw new Error('Naughty Naughty, please do not use this board name');
  }

  if (boardName.trim().length > BOARD_CONFIG.MAX_BOARD_NAME_LENGTH) {
    throw new Error(`Board name cannot be longer than ${BOARD_CONFIG.MAX_BOARD_NAME_LENGTH} characters`);
  }

  const wishlist = await getWishlist();

  const boardExists = wishlist.boards.some((board) => board.name.toLowerCase() === boardName.toLowerCase());
  if (boardExists) {
    throw new Error(`Board with name "${boardName}" already exists`);
  }

  const newBoardId = getNextAvailableBoardId(wishlist.boards);

  if (!newBoardId) {
    throw new Error(`Maximum number of boards reached (${BOARD_CONFIG.MAX_BOARDS}). Cannot create more boards.`);
  }

  const newBoard = {
    id: newBoardId,
    name: boardName,
    colour: colour,
    emoji: emoji,
    items: [],
    active: true,
    createdAt: new Date().toISOString(),
  };

  const updatedWishlist = {
    ...wishlist,
    boards: [...wishlist.boards, newBoard],
  };

  await saveWishlist(updatedWishlist);

  if (isLoggedIn()) {
    addPendingOperation(OPERATION_TYPES.CREATE_BOARD, `board_${newBoardId}`, newBoardId, null, {
      boardId: newBoardId,
      boardName: boardName,
      colour: colour,
      emoji: emoji,
    });

    await sendWishlistUpdate(OPERATION_TYPES.CREATE_BOARD, {
      boardId: newBoardId,
      boardName: boardName,
      colour: colour,
      emoji: emoji,
    });
  }

  const createBoardEvent = new CustomEvent('wishlist:create-board', {
    detail: {
      boardId: newBoardId,
      boardName,
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(createBoardEvent);

  invalidateCache();

  const updateEvent = new CustomEvent('wishlist:update', {
    detail: {
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(updateEvent);

  return updatedWishlist;
};

/**
 * Edit a board in the wishlist (name, colour, emoji)
 * @param {number} boardId - ID of the board to edit (required)
 * @param {Object} options - Edit options
 * @param {string} [options.boardName] - New board name (optional)
 * @param {string} [options.colour] - New board colour hex code (optional)
 * @param {string} [options.emoji] - New board emoji (optional)
 * @fires wishlist:edit-board
 * @returns {Promise<Object>} Updated wishlist
 * @throws {Error} If boardId not provided, board not found, or no properties provided
 */
const editBoard = async (
  boardId,
  {
    boardName,
    colour,
    emoji,
  } = {},
) => {
  // Frontend validation - ensure at least one property is provided
  if (boardName === undefined && colour === undefined && emoji === undefined) {
    throw new Error('At least one property (boardName, colour, emoji) must be provided');
  }

  if (!boardId) {
    throw new Error('Board ID is required');
  }

  if (boardName && isSuspicious(boardName)) {
    throw new Error('Naughty Naughty, please do not use this board name');
  }

  if (boardName && boardName.trim().length > BOARD_CONFIG.MAX_BOARD_NAME_LENGTH) {
    throw new Error(`Board name cannot be longer than ${BOARD_CONFIG.MAX_BOARD_NAME_LENGTH} characters`);
  }

  const wishlist = await getWishlist();

  const board = wishlist.boards.find((board) => board.id === boardId);
  if (!board) {
    throw new Error(`Board ${boardId} not found`);
  }

  // Check if any values are actually different (diff checker)
  const hasChanges = (boardName !== undefined && board.name !== boardName) || (colour !== undefined && board.colour !== colour) || (emoji !== undefined && board.emoji !== emoji);

  if (!hasChanges) {
    throw new Error('Please make at least one change before saving');
  }

  // Check if new name already exists (exclude current board)
  if (boardName !== undefined) {
    const boardExists = wishlist.boards.some((b) => b.name.toLowerCase() === boardName.toLowerCase() && b.id !== boardId);
    if (boardExists) {
      throw new Error(`Board with name "${boardName}" already exists`);
    }
  }

  // Create updated board object
  const updatedBoard = {
    ...board,
    ...(boardName !== undefined && { name: boardName }),
    ...(colour !== undefined && { colour: colour }),
    ...(emoji !== undefined && { emoji: emoji }),
  };

  const updatedWishlist = {
    ...wishlist,
    boards: wishlist.boards.map((b) =>
      b.id === boardId
        ? updatedBoard
        : b,
    ),
  };

  await saveWishlist(updatedWishlist);

  if (isLoggedIn()) {
    const payload = {
      boardId: boardId,
      ...(boardName !== undefined && { boardName: boardName }),
      ...(colour !== undefined && { colour: colour }),
      ...(emoji !== undefined && { emoji: emoji }),
    };

    addPendingOperation(OPERATION_TYPES.EDIT_BOARD, `board_${boardId}`, boardId, null, payload);

    await sendWishlistUpdate(OPERATION_TYPES.EDIT_BOARD, payload);
  }

  const editBoardEvent = new CustomEvent('wishlist:edit-board', {
    detail: {
      boardId: boardId,
      boardName: updatedBoard.name,
      colour: updatedBoard.colour,
      emoji: updatedBoard.emoji,
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(editBoardEvent);

  invalidateCache();

  const updateEvent = new CustomEvent('wishlist:update', {
    detail: {
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(updateEvent);

  return updatedWishlist;
};

/**
 * Renames a board in the wishlist
 * @param {number} boardId - ID of the board to rename
 * @param {string} newName - New name for the board
 * @fires wishlist:rename-board
 * @returns {Promise<Object>} Updated wishlist
 */
const renameBoard = async (boardId, newName) => {
  if (!newName) {
    console.warn('New board name is required');
    return;
  }

  const wishlist = await getWishlist();

  const nameExists = wishlist.boards.some((board) => board.name === newName && board.id !== boardId);
  if (nameExists) {
    console.warn(`Board with name "${newName}" already exists`);
    return wishlist;
  }

  const boardIndex = wishlist.boards.findIndex((board) => board.id === boardId);
  if (boardIndex === -1) {
    console.warn(`Board with ID ${boardId} not found`);
    return wishlist;
  }

  const oldName = wishlist.boards[boardIndex].name;
  const updatedBoards = [...wishlist.boards];
  updatedBoards[boardIndex] = {
    ...updatedBoards[boardIndex],
    name: newName,
  };

  const updatedWishlist = {
    ...wishlist,
    boards: updatedBoards,
  };

  await saveWishlist(updatedWishlist);

  if (isLoggedIn()) {
    addPendingOperation(OPERATION_TYPES.RENAME_BOARD, `board_${boardId}`, boardId, null, {
      boardId: boardId,
      oldName: oldName,
      newName: newName,
    });

    await sendWishlistUpdate(OPERATION_TYPES.RENAME_BOARD, {
      boardId: boardId,
      oldName: oldName,
      newName: newName,
    });
  }

  const renameBoardEvent = new CustomEvent('wishlist:rename-board', {
    detail: {
      boardId,
      oldName,
      newName,
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(renameBoardEvent);

  invalidateCache();

  return updatedWishlist;
};

/**
 * Moves one or multiple products from one board to one or multiple boards
 * @param {string|Array} productIdOrItems - Single product ID (legacy) or array of items [{productId}, ...]
 * @param {number} fromBoardId - Source board ID
 * @param {number|Array<number>} toBoardIds - Destination board ID(s) - can be single number or array
 * @fires wishlist:move-product
 * @returns {Promise<Object>} Updated wishlist with success status, message, movedCount, and board details
 * @example
 * // Legacy single product, single destination
 * moveProductToBoard("123", 1, 2)
 *
 * // Multiple products, single destination (backward compatible)
 * moveProductToBoard([{productId: "123"}, {productId: "456"}], 1, 2)
 *
 * // Single product, multiple destinations
 * moveProductToBoard("123", 1, [2, 3, 4])
 *
 * // Multiple products, multiple destinations
 * moveProductToBoard([{productId: "123"}, {productId: "456"}], 1, [2, 3, 4])
 */
const moveProductToBoard = async (productIdOrItems, fromBoardId, toBoardIds) => {
  if (!fromBoardId || !toBoardIds) {
    throw new Error('fromBoardId and toBoardIds are required');
  }

  // Convert single toBoardIds to array if needed (backward compatibility)
  let targetBoardIds;
  if (Array.isArray(toBoardIds)) {
    targetBoardIds = toBoardIds;
  } else if (typeof toBoardIds === 'number') {
    targetBoardIds = [toBoardIds];
  } else {
    throw new Error('toBoardIds must be a number or array of numbers');
  }

  // Check if source board is in destination boards
  if (targetBoardIds.includes(fromBoardId)) {
    throw new Error('Source board cannot be in destination boards');
  }

  // Handle backward compatibility - convert single productId to items array
  let productsToMove;
  if (Array.isArray(productIdOrItems)) {
    productsToMove = productIdOrItems;
  } else if (typeof productIdOrItems === 'string') {
    productsToMove = [{ productId: productIdOrItems }];
  } else {
    throw new Error('First parameter must be either a productId string or items array');
  }

  // Validate all items have productId
  for (const item of productsToMove) {
    if (!item.productId) {
      throw new Error('All items must have a productId');
    }
  }

  const wishlist = await getWishlist();

  const fromBoard = wishlist.boards.find((board) => board.id === fromBoardId);
  if (!fromBoard) {
    throw new Error('Source board not found');
  }

  // Find valid destination boards, skip invalid ones (don't fail entire operation)
  const validToBoards = [];
  const failedBoardIds = [];

  targetBoardIds.forEach((boardId) => {
    const board = wishlist.boards.find((b) => b.id === boardId);
    if (board) {
      validToBoards.push(board);
    } else {
      failedBoardIds.push(boardId);
    }
  });

  if (validToBoards.length === 0) {
    throw new Error('Please select at least one board to move to');
  }

  const productIdsToMove = new Set(productsToMove.map((item) => item.productId));
  const itemsToMove = [];

  // Find all items to move from the source board
  fromBoard.items.forEach((item) => {
    const { productId: itemProductId } = parseWishlistId(item);
    if (productIdsToMove.has(itemProductId)) {
      itemsToMove.push(item);
    }
  });

  if (itemsToMove.length === 0) {
    const productList = Array.from(productIdsToMove).join(', ');
    throw new Error(`Products [${productList}] not found in source board`);
  }

  // Remove items from source board
  fromBoard.items = fromBoard.items.filter((item) => {
    const { productId: itemProductId } = parseWishlistId(item);
    return !productIdsToMove.has(itemProductId);
  });

  let totalMovedCount = 0;
  const successfulBoardNames = [];

  // Add items to each valid destination board
  validToBoards.forEach((toBoard) => {
    if (!toBoard.items) {
      toBoard.items = [];
    }

    let boardMovedCount = 0;
    itemsToMove.forEach((item) => {
      if (!toBoard.items.includes(item)) {
        toBoard.items.push(item);
        boardMovedCount++;
      }
    });

    if (boardMovedCount > 0) {
      successfulBoardNames.push(toBoard.name);
      totalMovedCount += boardMovedCount;
    }
  });

  wishlist.count = calculateTotalItemCount(wishlist.boards);
  await saveWishlist(wishlist);

  if (isLoggedIn()) {
    // For move operations, we'll store the first productId as the identifier for pending operations
    const primaryProductId = productsToMove[0].productId;
    addPendingOperation(OPERATION_TYPES.MOVE_PRODUCT, `move_${primaryProductId}_${Date.now()}`, primaryProductId, null, {
      fromBoardId: fromBoardId,
      toBoardIds: targetBoardIds,
      items: productsToMove,
    });

    await sendWishlistUpdate(OPERATION_TYPES.MOVE_PRODUCT, {
      items: productsToMove,
      fromBoardId: fromBoardId,
      toBoardIds: targetBoardIds,
    });
  }

  const destinationNames = successfulBoardNames.join('", "');
  const failedMessage =
    failedBoardIds.length > 0
      ? ` (${failedBoardIds.length} boards not found)`
      : '';

  const message =
    productsToMove.length === 1
      ? `Moved product ${productsToMove[0].productId} from "${fromBoard.name}" to "${destinationNames}"${failedMessage}`
      : `Moved ${itemsToMove.length} items from "${fromBoard.name}" to ${validToBoards.length} boards${failedMessage}`;

  const moveProductEvent = new CustomEvent('wishlist:move-product', {
    detail: {
      success: true,
      message,
      movedCount: totalMovedCount,
      items: productsToMove,
      fromBoardId,
      toBoardIds: targetBoardIds,
      fromBoardName: fromBoard.name,
      toBoardNames: successfulBoardNames,
      successfulBoards: successfulBoardNames,
      failedBoardIds,
      totalDestinations: validToBoards.length,
      ...wishlist,
    },
  });
  document.dispatchEvent(moveProductEvent);

  invalidateCache();

  const updateEvent = new CustomEvent('wishlist:update', {
    detail: {
      ...wishlist,
    },
  });

  document.dispatchEvent(updateEvent);

  return {
    success: true,
    message,
    boards: wishlist.boards,
    movedCount: totalMovedCount,
    successfulBoards: successfulBoardNames,
    failedBoardIds,
    totalDestinations: validToBoards.length,
    ...wishlist,
  };
};

/**
 * Checks if a product is in the wishlist
 * @param {string} productId - ID of product to check
 * @returns {Promise<boolean>} True if product is in wishlist, false otherwise
 */
const productIsIn = async (productId) => {
  if (!productId) return false;

  try {
    const wishlist = await getWishlist();
    return productIdsHelper.has(wishlist.allProductIds, productId);
  } catch (error) {
    return false;
  }
};

/**
 * Merges metafield wishlist data with IndexedDB wishlist, prioritizing local items.
 * Local items are considered "newer" and get priority over server items.
 * For conflicts (same product, different variant), local variant wins.
 *
 * @param {Object} metafieldWishlist - Wishlist from metafield (server truth)
 * @param {Object} indexedDBWishlist - Wishlist from IndexedDB (local cache)
 * @param {Array<{type:string,productId:string,productId:string,variantId?:string}>} pendingOps - Pending operations
 * @returns {Object} Merged wishlist
 */
const mergeWishlists = (metafieldWishlist, indexedDBWishlist, pendingOps = []) => {
  const pendingIncludeIds = new Set(
    (pendingOps || [])
      .filter((op) => op && (op.type === 'add' || op.type === 'updateVariant'))
      .map((op) => op.productId),
  );

  const metaBoards = metafieldWishlist.boards || [];
  const indexedBoards = indexedDBWishlist.boards || [];

  const metaBoardsMap = new Map();
  metaBoards.forEach((board) => {
    if (board.id) {
      metaBoardsMap.set(board.id, { ...board });
    }
  });

  const mergedBoards = [...metaBoards];
  indexedBoards.forEach((indexedBoard) => {
    if (indexedBoard.id && !metaBoardsMap.has(indexedBoard.id)) {
      const hasPendingItems = indexedBoard.items?.some((item) => {
        const { productId } = parseWishlistId(item);
        return pendingIncludeIds.has(productId);
      });

      if (hasPendingItems) {
        mergedBoards.push({ ...indexedBoard });
        metaBoardsMap.set(indexedBoard.id, { ...indexedBoard });
      }
    }
  });

  const finalBoards = mergedBoards.map((board) => {
    const metaBoard = metaBoards.find((b) => b.id === board.id);
    const indexedBoard = indexedBoards.find((b) => b.id === board.id);

    const metaItems = metaBoard?.items || [];
    const indexedItems = indexedBoard?.items || [];

    const productMap = new Map();

    metaItems.forEach((item) => {
      const { productId } = parseWishlistId(item);
      productMap.set(productId, { item, source: 'server' });
    });

    indexedItems.forEach((item) => {
      const { productId } = parseWishlistId(item);

      if (pendingIncludeIds.has(productId)) {
        productMap.set(productId, { item, source: 'local' });
      }
    });

    const serverItems = [];
    const localItems = [];

    for (const [productId, { item, source }] of productMap) {
      if (source === 'server') {
        const hasLocalOverride = indexedItems.some((localItem) => {
          const { productId: localProductId } = parseWishlistId(localItem);
          return localProductId === productId && pendingIncludeIds.has(localProductId);
        });

        if (!hasLocalOverride) {
          serverItems.push(item);
        }
      } else {
        localItems.push(item);
      }
    }

    const finalItems = [...serverItems, ...localItems];

    return {
      ...board,
      items: finalItems,
    };
  });

  if (finalBoards.length === 0) {
    finalBoards.push(createDefaultBoard());
  }

  const allProductIdsSet = new Set();
  finalBoards.forEach((board) => {
    if (board.items && board.active) {
      board.items.forEach((item) => {
        const { productId } = parseWishlistId(item);
        allProductIdsSet.add(productId);
      });
    }
  });

  const productData = {};
  const metaProductData = metafieldWishlist.productData || {};
  const localProductData = indexedDBWishlist.productData || {};

  Object.keys(metaProductData).forEach((id) => {
    productData[id] = { ...metaProductData[id] };
  });

  Object.keys(localProductData).forEach((id) => {
    if (allProductIdsSet.has(id) && pendingIncludeIds.has(id)) {
      productData[id] = { ...localProductData[id] };
    }
  });

  const mergedWishlist = {
    id: 'wishlist',
    count: calculateTotalItemCount(finalBoards),
    allProductIds: productIdsHelper.fromArray(Array.from(allProductIdsSet)),
    productData,
    boards: finalBoards,
  };

  const hasStaleData = checkAndCleanStaleLocalData(indexedDBWishlist, mergedWishlist, pendingIncludeIds);

  if (hasStaleData) {
    saveWishlist(mergedWishlist).catch(() => { });
  }

  return mergedWishlist;
};

/**
 * Checks if local data has stale products and needs cleanup
 * @param {Object} indexedDBWishlist - Local wishlist data
 * @param {Object} mergedWishlist - Final merged wishlist
 * @param {Set} pendingIncludeIds - Set of product IDs with pending operations
 * @returns {boolean} True if stale data was found and needs cleanup
 */
const checkAndCleanStaleLocalData = (indexedDBWishlist, mergedWishlist, pendingIncludeIds) => {
  let hasStaleData = false;

  const localProductData = indexedDBWishlist.productData || {};
  const mergedProductData = mergedWishlist.productData || {};

  Object.keys(localProductData).forEach((productId) => {
    if (!mergedProductData[productId] && !pendingIncludeIds.has(productId)) {
      hasStaleData = true;
    }
  });

  const localBoards = indexedDBWishlist.boards || [];
  const mergedBoardsMap = new Map();

  mergedWishlist.boards.forEach((board) => {
    mergedBoardsMap.set(board.id, new Set(board.items || []));
  });

  localBoards.forEach((localBoard) => {
    if (!localBoard.items) return;

    const mergedBoardItems = mergedBoardsMap.get(localBoard.id) || new Set();

    localBoard.items.forEach((item) => {
      const { productId } = parseWishlistId(item);

      if (!mergedBoardItems.has(item) && !pendingIncludeIds.has(productId)) {
        hasStaleData = true;
      }
    });
  });

  localBoards.forEach((localBoard) => {
    const mergedBoard = mergedWishlist.boards.find((b) => b.id === localBoard.id);

    if (!mergedBoard && localBoard.items && localBoard.items.length > 0) {
      const hasPendingItems = localBoard.items.some((item) => {
        const { productId } = parseWishlistId(item);
        return pendingIncludeIds.has(productId);
      });

      if (!hasPendingItems) {
        hasStaleData = true;
      }
    }
  });

  return hasStaleData;
};

/**
 * Gets pending operations from localStorage
 * @returns {Array} Array of pending operations
 */
const getPendingOperations = () => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_OPERATIONS_KEY) || '[]');
  } catch (error) {
    return [];
  }
};

/**
 * Saves pending operations to localStorage
 * @param {Array} operations - Array of pending operations
 */
const savePendingOperations = (operations) => {
  try {
    localStorage.setItem(PENDING_OPERATIONS_KEY, JSON.stringify(operations));
  } catch (error) { }
};

/**
 * Removes successful operations from the pending queue
 * @param {Array<string>} successfulOperationIds - Array of operation IDs that were successful
 */
const cleanupSuccessfulOperations = (successfulOperationIds) => {
  if (!successfulOperationIds.length) return;

  const operations = getPendingOperations();
  const successfulSet = new Set(successfulOperationIds);

  const remainingOperations = operations.filter((op) => !successfulSet.has(op.id));

  savePendingOperations(remainingOperations);
};

/**
 * Adds a pending operation with optimized deduplication using Sets
 * @param {string} type - Operation type ('add', 'remove', 'updateVariant', 'createBoard', 'deleteBoard', 'renameBoard', 'moveProduct')
 * @param {string} productHandle - Product handle (or primary identifier for non-product operations)
 * @param {string} productId - Product ID (or secondary identifier)
 * @param {string} [variantId] - Variant ID (optional)
 * @param {Object} [extraData] - Additional data for board operations (e.g., boardId, boardName)
 * @returns {string} Operation ID
 */
const addPendingOperation = (type, productHandle, productId, variantId = null, extraData = {}) => {
  const operationId = `${type}_${productId}_${Date.now()}`;
  const operations = getPendingOperations();

  const updateVariantOps = new Set();
  const addOps = new Set();
  const removeOps = new Set();
  const removeAllItemsOps = new Set();

  operations.forEach((op) => {
    const key = op.productId;
    if (op.type === 'updateVariant') updateVariantOps.add(key);
    else if (op.type === 'add') addOps.add(key);
    else if (op.type === 'remove') removeOps.add(key);
    else if (op.type === 'removeAllItems') removeAllItemsOps.add(key);
  });

  let filteredOperations;

  if (type === 'updateVariant') {
    filteredOperations = operations.filter((op) => !(op.type === 'updateVariant' && op.productId === productId));
  } else if (type === 'remove') {
    filteredOperations = operations.filter((op) => !((op.type === 'add' && op.productId === productId && op.boardId === (extraData.boardId || 1)) || (op.type === 'updateVariant' && op.productId === productId && op.boardId === (extraData.boardId || 1))));
  } else if (type === 'removeAllItems') {
    filteredOperations = operations.filter((op) => !((op.type === 'add' || op.type === 'updateVariant') && op.productId === productId));
  } else if (type === 'add') {
    filteredOperations = operations.filter((op) => !((op.type === 'remove' && op.productId === productId && op.boardId === (extraData.boardId || 1)) || (op.type === 'removeAllItems' && op.productId === productId)));
  } else if (type === 'createBoard') {
    filteredOperations = operations.filter((op) => !(op.type === 'deleteBoard' && op.boardId === extraData.boardId));
  } else if (type === 'deleteBoard') {
    filteredOperations = operations.filter((op) => !(op.type === 'createBoard' && op.boardId === extraData.boardId));
  } else if (type === 'renameBoard') {
    filteredOperations = operations.filter((op) => !(op.type === 'renameBoard' && op.boardId === extraData.boardId));
  } else if (type === 'editBoard') {
    filteredOperations = operations.filter((op) => !(op.type === 'editBoard' && op.boardId === extraData.boardId));
  } else {
    filteredOperations = operations;
  }

  filteredOperations.push({
    id: operationId,
    type,
    productId: productId,
    variantId,
    ...(type != 'updateVariant' && { boardId: extraData.boardId }),
    timestamp: Date.now(),
    retryCount: 0,
    ...extraData,
  });

  savePendingOperations(filteredOperations);
  return operationId;
};

/**
 * Retries operations with exponential backoff and retry count tracking
 * Uses 2-retry policy with 10-minute base delay and 2-hour TTL
 * @param {Array} operations - Array of pending operations
 * @returns {Promise<void>}
 */
const retryOldOperations = async (operations) => {
  if (!isLoggedIn()) return;

  const now = Date.now();
  const maxRetries = 2;
  const baseRetryDelay = 10 * 60 * 1000;
  const maxOperationAge = 2 * 60 * 60 * 1000;

  const validOperations = operations.filter((op) => {
    const age = now - op.timestamp;
    return age <= maxOperationAge;
  });

  const operationsToRetry = validOperations.filter((op) => {
    const retryCount = op.retryCount || 0;
    if (retryCount >= maxRetries) return false;

    const retryDelay = baseRetryDelay * Math.pow(2, retryCount);
    const timeSinceLastAttempt = now - (op.lastRetryAttempt || op.timestamp);

    return timeSinceLastAttempt > retryDelay;
  });

  const expiredCount = operations.length - validOperations.length;
  if (expiredCount > 0) {
    savePendingOperations(validOperations);
  }

  if (operationsToRetry.length > 0) {
    const updatedOperations = validOperations.map((op) => {
      if (operationsToRetry.includes(op)) {
        return {
          ...op,
          retryCount: (op.retryCount || 0) + 1,
          lastRetryAttempt: now,
        };
      }
      return op;
    });

    savePendingOperations(updatedOperations);

    const retryOperations = operationsToRetry.map((op) => ({
      id: op.id,
      operation: op.type,
      productId: op.productId,
      variantId: op.variantId,
      boardId: op.boardId || 1,
    }));

    try {
      const result = await sendBulkWishlistOperations(retryOperations);
    } catch (error) {
      console.error('Retry operations failed:', error);
    }
  }

  const finalValidOperations = validOperations.filter((op) => (op.retryCount || 0) < maxRetries);
  if (finalValidOperations.length !== validOperations.length) {
    const exceededRetryCount = validOperations.length - finalValidOperations.length;
    savePendingOperations(finalValidOperations);
    console.warn(`Removed ${exceededRetryCount} operations that exceeded max retry attempts (${maxRetries} retries)`);
  }
};

/**
 * Cleans up completed operations by comparing against metafield state
 * @param {Object} metafieldWishlist - Current metafield wishlist
 * @returns {Array} Remaining pending operations
 */
const cleanupCompletedOperations = (metafieldWishlist) => {
  const operations = getPendingOperations();
  const stillPending = [];
  const completed = [];

  operations.forEach((op) => {
    let isCompleted = false;

    if (op.type === 'remove') {
      // Board-specific remove: check if product is removed from the specific board
      const targetBoard = metafieldWishlist.boards?.find((board) => board.id === op.boardId);
      if (targetBoard) {
        const productInBoard = targetBoard.items?.some((item) => {
          const { productId } = parseWishlistId(item);
          return productId === op.productId;
        });
        isCompleted = !productInBoard;
      } else {
        isCompleted = true; // If board doesn't exist, remove is considered complete
      }
    } else if (op.type === 'removeAllItems') {
      // Remove from all boards: check if product is completely removed from allProductIds
      isCompleted = !productIdsHelper.has(metafieldWishlist.allProductIds, op.productId);
    } else if (op.type === 'add') {
      if (productIdsHelper.has(metafieldWishlist.allProductIds, op.productId)) {
        const productData = metafieldWishlist.productData?.[op.productId];
        if (productData) {
          const expectedVariantId = op.variantId
            ? String(op.variantId)
            : null;
          const actualVariantId = productData.selectedVariantId
            ? String(productData.selectedVariantId)
            : null;
          isCompleted = expectedVariantId === actualVariantId;
        } else {
          isCompleted = false;
        }
      } else {
        isCompleted = false;
      }
    } else if (op.type === 'updateVariant') {
      if (productIdsHelper.has(metafieldWishlist.allProductIds, op.productId)) {
        const productData = metafieldWishlist.productData?.[op.productId];
        if (productData) {
          const expectedVariantId = op.variantId
            ? String(op.variantId)
            : null;
          const actualVariantId = productData.selectedVariantId
            ? String(productData.selectedVariantId)
            : null;
          isCompleted = expectedVariantId === actualVariantId;
        } else {
          isCompleted = false;
        }
      } else {
        isCompleted = true;
      }
    } else if (op.type === 'createBoard') {
      const boardExists = metafieldWishlist.boards?.find((board) => board.id === op.boardId);
      isCompleted = !!boardExists;
    } else if (op.type === 'deleteBoard') {
      const boardExists = metafieldWishlist.boards?.find((board) => board.id === op.boardId);
      isCompleted = !boardExists;
    } else if (op.type === 'renameBoard') {
      const board = metafieldWishlist.boards?.find((board) => board.id === op.boardId);
      if (board) {
        isCompleted = board.name === op.newName;
      } else {
        isCompleted = true; // If board doesn't exist, rename operation is considered complete
      }
    } else if (op.type === 'editBoard') {
      const board = metafieldWishlist.boards?.find((board) => board.id === op.boardId);
      if (board && op.data) {
        let matches = true;
        if (op.data.boardName !== undefined) matches = matches && board.name === op.data.boardName;
        if (op.data.colour !== undefined) matches = matches && board.colour === op.data.colour;
        if (op.data.emoji !== undefined) matches = matches && board.emoji === op.data.emoji;
        isCompleted = matches;
      } else {
        isCompleted = true; // If board doesn't exist or no data, edit operation is considered complete
      }
    } else if (op.type === 'moveProduct') {
      const fromBoard = metafieldWishlist.boards?.find((board) => board.id === op.fromBoardId);
      const toBoard = metafieldWishlist.boards?.find((board) => board.id === op.toBoardId);
      if (fromBoard && toBoard) {
        // Check if this is a new items-based move operation or legacy single product move
        if (op.items && Array.isArray(op.items)) {
          // New items-based move operation
          let allItemsMoved = true;
          for (const item of op.items) {
            const itemExistsInFromBoard = fromBoard.items?.some((boardItem) => {
              const { productId } = parseWishlistId(boardItem);
              return productId === item.productId;
            });
            const itemExistsInToBoard = toBoard.items?.some((boardItem) => {
              const { productId } = parseWishlistId(boardItem);
              return productId === item.productId;
            });
            if (itemExistsInFromBoard || !itemExistsInToBoard) {
              allItemsMoved = false;
              break;
            }
          }
          isCompleted = allItemsMoved;
        } else {
          // Legacy single product move operation
          const itemExistsInFromBoard = fromBoard.items?.some((item) => {
            const { productId } = parseWishlistId(item);
            return productId === op.productId;
          });
          const itemExistsInToBoard = toBoard.items?.some((item) => {
            const { productId } = parseWishlistId(item);
            return productId === op.productId;
          });
          isCompleted = !itemExistsInFromBoard && itemExistsInToBoard;
        }
      } else {
        isCompleted = true; // If either board doesn't exist, move operation is considered complete
      }
    }

    if (isCompleted) {
      completed.push(op);
    } else {
      stillPending.push(op);
    }
  });

  if (stillPending.length > 0) {
    retryOldOperations(stillPending);
  }

  savePendingOperations(stillPending);
  return stillPending;
};

/**
 * Applies pending operations to metafield wishlist data
 * @param {Object} metafieldWishlist - Metafield wishlist data
 * @param {Array} pendingOps - Array of pending operations
 * @returns {Object} Adjusted wishlist with pending operations applied and limited to 128 items
 */
const applyPendingOperations = (metafieldWishlist, pendingOps) => {
  let adjusted = { ...metafieldWishlist };

  pendingOps.forEach((op) => {
    if (op.type === 'remove') {
      // Board-specific remove: remove from specific board only
      if (adjusted.boards) {
        adjusted.boards = adjusted.boards.map((board) => {
          if (board.id === op.boardId) {
            return {
              ...board,
              items: board.items
                ? board.items.filter((item) => {
                  const { productId } = parseWishlistId(item);
                  return productId !== op.productId;
                })
                : [],
            };
          }
          return board;
        });
      }

      // Check if product is still in other boards, if not remove from allProductIds and productData
      const productInOtherBoards = adjusted.boards?.some(
        (board) =>
          board.id !== op.boardId &&
          board.items?.some((item) => {
            const { productId } = parseWishlistId(item);
            return productId === op.productId;
          }),
      );

      if (!productInOtherBoards) {
        productIdsHelper.remove(adjusted.allProductIds, op.productId);
        if (adjusted.productData && adjusted.productData[op.productId]) {
          delete adjusted.productData[op.productId];
        }
      }
    } else if (op.type === 'removeAllItems') {
      // Remove from all boards: remove from all boards and cleanup allProductIds/productData
      productIdsHelper.remove(adjusted.allProductIds, op.productId);
      if (adjusted.productData && adjusted.productData[op.productId]) {
        delete adjusted.productData[op.productId];
      }

      if (adjusted.boards) {
        adjusted.boards = adjusted.boards.map((board) => ({
          ...board,
          items: board.items
            ? board.items.filter((item) => {
              const { productId } = parseWishlistId(item);
              return productId !== op.productId;
            })
            : [],
        }));
      }
    } else if (op.type === 'add') {
      if (!productIdsHelper.has(adjusted.allProductIds, op.productId)) {
        productIdsHelper.add(adjusted.allProductIds, op.productId);
        if (!adjusted.productData) adjusted.productData = {};
        adjusted.productData[op.productId] = {
          productId: op.productId,
          selectedVariantId: op.variantId,
        };

        if (!adjusted.boards || adjusted.boards.length === 0) {
          adjusted.boards = [createDefaultBoard()];
        }
        const defaultBoard = adjusted.boards.find((board) => board.active) || adjusted.boards[0];
        if (defaultBoard && defaultBoard.items) {
          const itemId = op.variantId
            ? formatWishlistId(op.productId, op.variantId)
            : op.productId;
          defaultBoard.items.push(itemId);
        }
      }
    } else if (op.type === 'updateVariant') {
      if (productIdsHelper.has(adjusted.allProductIds, op.productId)) {
        if (!adjusted.productData) adjusted.productData = {};
        if (adjusted.productData[op.productId]) {
          adjusted.productData[op.productId].selectedVariantId = op.variantId;
        }

        if (adjusted.boards) {
          adjusted.boards = adjusted.boards.map((board) => ({
            ...board,
            items: board.items
              ? board.items.map((item) => {
                const { productId } = parseWishlistId(item);
                if (productId === op.productId) {
                  return formatWishlistId(op.productId, op.variantId);
                }
                return item;
              })
              : [],
          }));
        }
      }
    }
  });

  adjusted.count = calculateTotalItemCount(adjusted.boards);

  return adjusted;
};

/**
 * Fetches metafield wishlist data from the wishlist endpoint and merges with local data
 * Handles board creation and adds local items to the front of boards
 * @returns {Promise<Object|null>} Parsed wishlist or null if not found/invalid
 */
const parseMetafieldWishlist = async () => {
  if (!isLoggedIn()) {
    return null;
  }

  try {
    const response = await fetch(`${window.Shopify.routes.root}pages/my-wishlist?view=nolayout.wishlist`);

    if (!response.ok) {
      return null;
    }

    const wishlistData = await response.json();

    if (!wishlistData || typeof wishlistData !== 'object') {
      return null;
    }

    const localWishlist = await getWishlist();

    let boards = wishlistData.boards || [];

    if (!Array.isArray(boards)) {
      boards = [];
    }

    if (boards.length === 0) {
      boards = [createDefaultBoard()];
    } else {
      const hasDefaultBoard = boards.some((board) => board.id === BOARD_CONFIG.DEFAULT_BOARD_ID);
      if (!hasDefaultBoard) {
        boards.unshift(createDefaultBoard());
      }
    }

    const allIds = new Set();
    boards.forEach((board) => {
      if (board.items && Array.isArray(board.items) && board.active) {
        board.items.forEach((item) => {
          const { productId } = parseWishlistId(item);
          allIds.add(productId);
        });
      }
    });

    const allProductIds = productIdsHelper.fromArray(Array.from(allIds));

    const mergedProductData = { ...(wishlistData.productData || {}) };

    const metafieldProductData = boards.forEach((board) => {
      if (board.items && Array.isArray(board.items)) {
        board.items.forEach((item) => {
          const { productId, variantId } = parseWishlistId(item);
          if (productId && variantId) {
            if (!mergedProductData[productId]) {
              mergedProductData[productId] = {
                productId: String(productId),
                selectedVariantId: String(variantId),
              };
            } else if (!mergedProductData[productId].selectedVariantId) {
              mergedProductData[productId].selectedVariantId = String(variantId);
            }
          }
        });
      }
    });

    if (localWishlist.productData) {
      Object.keys(localWishlist.productData).forEach((productId) => {
        if (!mergedProductData[productId]) {
          mergedProductData[productId] = localWishlist.productData[productId];
        }
      });
    }

    const metafieldWishlist = {
      id: 'wishlist',
      count: calculateTotalItemCount(boards),
      allProductIds,
      productData: mergedProductData,
      boards: boards,
    };

    const stillPending = cleanupCompletedOperations(metafieldWishlist);
    return applyPendingOperations(metafieldWishlist, stillPending);
  } catch (error) {
    console.error('Error parsing metafield wishlist:', error);
    return null;
  }
};

/**
 * Groups operations by board ID for organized processing
 * @param {Array} operations - Array of pending operations
 * @returns {Object} Operations grouped by board ID
 */
const groupOperationsByBoard = (operations) => {
  return operations.reduce((boards, op) => {
    const boardId = op.boardId || 1;
    if (!boards[boardId]) {
      boards[boardId] = [];
    }
    boards[boardId].push(op);
    return boards;
  }, {});
};

/**
 * Orders operations within a board by priority (delete > create > add/remove/update)
 * @param {Array} operations - Array of operations for a single board
 * @returns {Array} Ordered operations
 */
const orderOperationsByPriority = (operations) => {
  const priority = {
    deleteBoard: 1,
    createBoard: 2,
    editBoard: 2,
    renameBoard: 3,
    add: 4,
    remove: 4,
    updateVariant: 4,
    moveProduct: 5,
  };

  return operations.sort((a, b) => {
    const aPriority = priority[a.type] || 999;
    const bPriority = priority[b.type] || 999;
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    return a.timestamp - b.timestamp;
  });
};

/**
 * Sends any pending wishlist operations to the backend immediately, grouped by board ID
 * and processed in the correct order, then cleans up completed operations.
 * @returns {Promise<void>}
 */
const flushPendingWishlistOperations = async () => {
  const pendingOps = getPendingOperations();
  if (!pendingOps || pendingOps.length === 0) {
    return;
  }

  try {
    const boardGroups = groupOperationsByBoard(pendingOps);

    for (const [boardId, boardOps] of Object.entries(boardGroups)) {
      const orderedOps = orderOperationsByPriority(boardOps);

      const items = orderedOps.map((op) => ({
        operation: op.type,
        productId: op.productId,
        variantId: op.variantId,
        boardId: parseInt(boardId),
      }));

      await sendWishlistBeacons(items);
    }
  } catch (e) {
    console.error('Failed to flush pending operations:', e);
  }

  try {
    const latestServer = await parseMetafieldWishlist();
    if (latestServer) {
      cleanupCompletedOperations(latestServer);
    }
  } catch (e) { }
};

/**
 * Gets login state information
 * @returns {Object} State information
 */
const getLoginState = () => {
  const currentLoginState = isLoggedIn() ?? false;
  const storedLoginState = localStorage.getItem(LOGIN_STATE_KEY) === 'true';
  const justLoggedIn = !storedLoginState && currentLoginState;
  const justLoggedOut = storedLoginState && !currentLoginState;

  return {
    currentLoginState,
    justLoggedIn,
    justLoggedOut,
  };
};

/**
 * Handles wishlist initialization for logged in users
 * @param {Object} loginState - Login state information
 * @returns {Promise<Object>} Wishlist to save
 */
const handleLoggedInUser = async (loginState) => {
  const { justLoggedIn } = loginState;

  const existingWishlist = await getWishlist();

  if (justLoggedIn && existingWishlist.allProductIds && productIdsHelper.count(existingWishlist.allProductIds) > 0) {
    await convertLocalChangesToPendingOperations(existingWishlist);
  }

  const metafieldWishlist = await parseMetafieldWishlist();

  if (metafieldWishlist) {
    const pendingOps = getPendingOperations();
    const merged = mergeWishlists(metafieldWishlist, existingWishlist, pendingOps);

    await flushPendingWishlistOperations();

    return merged;
  }

  if (justLoggedIn && existingWishlist.allProductIds && productIdsHelper.count(existingWishlist.allProductIds) > 0) {
    await convertLocalChangesToPendingOperations(existingWishlist);
  }

  return existingWishlist;
};

/**
 * Clears IndexedDB wishlist data and pending operations
 * @returns {Promise<void>}
 */
const clearLocalWishlist = async () => {
  try {
    await saveWishlist(DEFAULT_WISHLIST);
    savePendingOperations([]);
    dispatchInitializeEvent(DEFAULT_WISHLIST);
  } catch (error) { }
};

/**
 * Handles wishlist initialization for logged out users
 * @param {Object} loginState - Login state information
 * @returns {Promise<Object>} Wishlist to save
 */
const handleLoggedOutUser = async (loginState) => {
  const { justLoggedOut } = loginState;

  if (justLoggedOut) {
    await clearLocalWishlist();
    return DEFAULT_WISHLIST;
  }

  const existingWishlist = await getWishlist();
  return existingWishlist;
};

/**
 * Dispatches wishlist initialize event
 * @param {Object} wishlist - Wishlist data to include in event
 */
const dispatchInitializeEvent = (wishlist) => {
  const event = new CustomEvent('wishlist:initialise', {
    detail: wishlist,
  });
  document.dispatchEvent(event);
};

/**
 * Initializes the wishlist from metafields and cleans stale data
 * Always processes metafield data to ensure fresh product information
 * @returns {Promise<void>}
 */
const initialiseWishlist = async () => {
  try {
    const loginState = getLoginState();

    localStorage.setItem(LOGIN_STATE_KEY, loginState.currentLoginState.toString());

    const wishlistToSave = loginState.currentLoginState
      ? await handleLoggedInUser(loginState)
      : await handleLoggedOutUser(loginState);

    await saveWishlist(wishlistToSave);
    dispatchInitializeEvent(wishlistToSave);
  } catch (error) {
    await saveWishlist(DEFAULT_WISHLIST);
    dispatchInitializeEvent(DEFAULT_WISHLIST);
  }
};

document.addEventListener('DOMContentLoaded', initialiseWishlist);
document.addEventListener('wishlist:variant-changed', async (event) => {
  const { variant, product } = event?.detail;

  if (variant && product && product.id) {
    await updateVariant(product.id, variant.id);
  }
});

/**
 * Manually forces cleanup of pending operations by fetching current state
 * Useful for debugging or forcing cleanup when automatic cleanup fails
 * @returns {Promise<void>}
 */
const forceCleanupPendingOperations = async () => {
  const operations = getPendingOperations();

  if (operations.length === 0) {
    return;
  }

  const metafieldWishlist = await parseMetafieldWishlist();
  if (metafieldWishlist) {
    cleanupCompletedOperations(metafieldWishlist);
  }
};

const removeFromAllBoards = async (productId) => {
  if (!productId) {
    return;
  }
  const wishlist = await getWishlist();
  let itemFound = false;

  const updatedBoards = wishlist.boards.map((board) => {
    if (board.items && board.items.length > 0) {
      const filteredItems = board.items.filter((item) => {
        const { productId: itemProductId } = parseWishlistId(item);
        return itemProductId !== productId;
      });
      if (filteredItems.length !== board.items.length) {
        itemFound = true;
      }
      return {
        ...board,
        items: filteredItems,
      };
    }
    return board;
  });

  if (!itemFound) {
    return wishlist;
  }

  if (wishlist.allProductIds) {
    productIdsHelper.remove(wishlist.allProductIds, productId);
  }

  if (wishlist.productData && wishlist.productData[productId]) {
    delete wishlist.productData[productId];
  }

  const updatedWishlist = {
    ...wishlist,
    count: calculateTotalItemCount(updatedBoards),
    boards: updatedBoards,
  };

  await saveWishlist(updatedWishlist);

  if (isLoggedIn()) {
    addPendingOperation(OPERATION_TYPES.REMOVE_ALL_ITEMS, productId, productId, null, {});
    await sendWishlistUpdate(OPERATION_TYPES.REMOVE_ALL_ITEMS, {
      items: [{ productId: productId }],
    });
  }

  const removeEvent = new CustomEvent('wishlist:remove', {
    detail: {
      productId,
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(removeEvent);
  return updatedWishlist;
};

const removeFromAllBoardsMultiple = async (productIds) => {
  if (!productIds || !Array.isArray(productIds) || productIds.length === 0) {
    return;
  }
  
  const wishlist = await getWishlist();
  let itemsFound = false;
  const productIdSet = new Set(productIds);

  const updatedBoards = wishlist.boards.map((board) => {
    if (board.items && board.items.length > 0) {
      const filteredItems = board.items.filter((item) => {
        const { productId: itemProductId } = parseWishlistId(item);
        return !productIdSet.has(itemProductId);
      });
      if (filteredItems.length !== board.items.length) {
        itemsFound = true;
      }
      return {
        ...board,
        items: filteredItems,
      };
    }
    return board;
  });

  if (!itemsFound) {
    return wishlist;
  }

  // Remove from global product IDs and product data
  if (wishlist.allProductIds) {
    for (const productId of productIds) {
      productIdsHelper.remove(wishlist.allProductIds, productId);
    }
  }

  if (wishlist.productData) {
    for (const productId of productIds) {
      if (wishlist.productData[productId]) {
        delete wishlist.productData[productId];
      }
    }
  }

  const updatedWishlist = {
    ...wishlist,
    count: calculateTotalItemCount(updatedBoards),
    boards: updatedBoards,
  };

  await saveWishlist(updatedWishlist);

  if (isLoggedIn()) {
    // Add pending operations for each product
    for (const productId of productIds) {
      addPendingOperation(OPERATION_TYPES.REMOVE_ALL_ITEMS, productId, productId, null, {});
    }
    
    await sendWishlistUpdate(OPERATION_TYPES.REMOVE_ALL_ITEMS, {
      items: productIds.map(productId => ({ productId })),
    });
  }

  const removeEvent = new CustomEvent('wishlist:remove', {
    detail: {
      productIds,
      ...updatedWishlist,
    },
  });
  document.dispatchEvent(removeEvent);
  return updatedWishlist;
};

window.wishlist = {
  add,
  remove,
  retrieve,
  deleteBoard,
  createBoard,
  editBoard,
  renameBoard,
  moveProductToBoard,
  parseWishlistId,
  productIsIn,
  getShareUrl,
  getProducts,
  updateVariant,
  forceCleanupPendingOperations,
  flushPendingWishlistOperations,
  BOARD_CONFIG,
  removeFromAllBoards,
  removeFromAllBoardsMultiple,
  invalidateCache,
  PRODUCTS_DYNAMIC_QUERY,
  convertGraphQLDynamicToLiquidProduct,
  refreshDynamicProductData,
};

export default {
  add,
  remove,
  retrieve,
  deleteBoard,
  createBoard,
  editBoard,
  renameBoard,
  moveProductToBoard,
  productIsIn,
  getShareUrl,
  DEFAULT_WISHLIST,
  getProducts,
  updateVariant,
  forceCleanupPendingOperations,
  flushPendingWishlistOperations,
  BOARD_CONFIG,
  STOREFRONT_ACCESS_TOKEN,
  STOREFRONT_API_URL,
  PRODUCTS_BULK_QUERY,
  PRODUCT_WITH_SIBLINGS_QUERY,
  PRODUCTS_DYNAMIC_QUERY,
  parseWishlistId,
  convertGraphQLProductToLiquidProduct,
  convertGraphQLDynamicToLiquidProduct,
  refreshDynamicProductData,
  removeFromAllBoards,
  removeFromAllBoardsMultiple,
  invalidateCache,
  refreshDynamicProductData
};


